import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, REQUIREMENTS_TABS, REQUIREMENTS_COLUMNS,
  REQUIREMENTS_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
  REQUIREMENTS_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

function BadgeRow({ badge, idx, total, onDelete, onReorder, onAddBefore, onAddAfter, onEdit, dragRef, onDropItem }) {
  const field = "processingBadges";
  return (
    <tr
      draggable="true"
      onDragStart={(e) => {
        dragRef.current = { type: "array", field, fromIndex: idx };
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
        e.currentTarget.style.opacity = "0.4";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        e.currentTarget.style.outline = "2px dashed #3b82f6";
        e.currentTarget.style.outlineOffset = "-2px";
      }}
      onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.style.opacity = "";
        e.currentTarget.style.outline = "";
        if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
        const { field: fromField, fromIndex } = dragRef.current;
        if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
        onDropItem(fromIndex, idx);
        dragRef.current = null;
      }}
      onDragEnd={(e) => {
        e.currentTarget.style.opacity = "";
        e.currentTarget.style.outline = "";
        dragRef.current = null;
      }}
    >
      <td>
        <div className="AdminOrderBtns">
          <button className="AdminMoveBtn" onClick={() => onReorder(idx, -1)} disabled={idx === 0} title="Move up">&#8593;</button>
          <button className="AdminMoveBtn" onClick={() => onReorder(idx, 1)} disabled={idx === total - 1} title="Move down">&#8595;</button>
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => onEdit(badge)} title="Edit" style={{marginLeft:4}}>&#9998;</button>
        </div>
      </td>
      <td style={{fontWeight:600}}>{badge.label}</td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:badge.color,display:"inline-block"}} />
          {badge.color}
        </span>
      </td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:badge.bg,display:"inline-block"}} />
          {badge.bg}
        </span>
      </td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:badge.border||badge.color,border:"1px solid #d1d5db",display:"inline-block"}} />
          {badge.border||badge.color}
        </span>
      </td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span className="AdminBadgePreview" style={{background:badge.bg,color:badge.color,border:"1px solid "+(badge.border||badge.color),borderRadius:6,padding:"2px 10px",fontSize:13,fontWeight:600,whiteSpace:"nowrap"}}>
            {badge.label}
          </span>
        </span>
      </td>
      <td>
        <div className="AdminActionBtns">
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => onAddBefore(idx)} title="Add row before">&#9650;+</button>
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => onAddAfter(idx)} title="Add row after">&#9660;+</button>
          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => onDelete(badge.id)} title="Delete">&#10005;</button>
        </div>
      </td>
    </tr>
  );
}

function FlagRow({ flag, idx, total, onEdit, onMoveUp, onMoveDown, onAddBefore, onAddAfter, onDelete, dragRef, onDropItem }) {
  const field = "visaFlags";
  return (
    <tr
      draggable="true"
      onDragStart={(e) => {
        dragRef.current = { type: "array", field, fromIndex: idx };
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
        e.currentTarget.style.opacity = "0.4";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        e.currentTarget.style.outline = "2px dashed #3b82f6";
        e.currentTarget.style.outlineOffset = "-2px";
      }}
      onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.style.opacity = "";
        e.currentTarget.style.outline = "";
        if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
        const { field: fromField, fromIndex } = dragRef.current;
        if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
        onDropItem(fromIndex, idx);
        dragRef.current = null;
      }}
      onDragEnd={(e) => {
        e.currentTarget.style.opacity = "";
        e.currentTarget.style.outline = "";
        dragRef.current = null;
      }}
    >
      <td>
        <div className="AdminOrderBtns">
          <button className="AdminMoveBtn" onClick={onMoveUp} disabled={idx === 0} title="Move up">&#8593;</button>
          <button className="AdminMoveBtn" onClick={onMoveDown} disabled={idx === total - 1} title="Move down">&#8595;</button>
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => onEdit(flag, idx)} title="Edit" style={{marginLeft:4}}>&#9998;</button>
        </div>
      </td>
      <td style={{fontWeight:600}}>{flag.label}</td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:flag.text,display:"inline-block",border:"1px solid #d1d5db"}} />
          {flag.text}
        </span>
      </td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:flag.bg,display:"inline-block",border:"1px solid #d1d5db"}} />
          {flag.bg}
        </span>
      </td>
      <td>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <span style={{width:16,height:16,borderRadius:4,background:flag.border||flag.text,display:"inline-block",border:"1px solid #d1d5db"}} />
          {flag.border||flag.text}
        </span>
      </td>
      <td>
        <span className="AdminBadgePreview" style={{background:flag.bg,color:flag.text,border:"1px solid "+(flag.border||flag.text),borderRadius:6,padding:"2px 10px",fontSize:13,fontWeight:600,whiteSpace:"nowrap"}}>
          {flag.label}
        </span>
      </td>
      <td>
        <div className="AdminActionBtns">
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={onAddBefore} title="Add row before">&#9650;+</button>
          <button className="AdminIconBtn AdminIconBtnBlue" onClick={onAddAfter} title="Add row after">&#9660;+</button>
          <button className="AdminIconBtn AdminIconBtnRed" onClick={onDelete} title="Delete">&#10005;</button>
        </div>
      </td>
    </tr>
  );
}


function VisaRouteFormOverlay({ editingItem, availableMetas, availableFlags, availableBadges, availableLoaderCheckpoints, availableTips, availableRequiredDocs, availableCountries, onSave, onClose }) {
  const [form, setForm] = useState({
    passportName: editingItem?.passportName || "",
    destinationName: editingItem?.destinationName || "",
    processingTime: editingItem?.processingTime || "",
    visaMeta: (editingItem?.visaMeta || []).map((m) => ({ ...m })),
    visaFlags: (editingItem?.visaFlags || []).map((f) => ({ ...f })),
    processingBadges: (editingItem?.processingBadges || []).map((b) => ({ ...b })),
    loaderCheckpoints: sortByPct((editingItem?.loaderCheckpoints || []).map((lc) => ({ ...lc }))),
    tips: (editingItem?.tips || []).map((t) => typeof t === "string" ? { label: t, text: t } : { label: t.label || t.tip_text || t.text || "", text: t.text || t.tip_text || "" }),
    documents: (editingItem?.documents || []).map((d) => ({ ...d })),
  });

  const [showPassportDropdown, setShowPassportDropdown] = useState(false);
  const [passportSearch, setPassportSearch] = useState("");
  const passportDropdownRef = useRef(null);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  const [destSearch, setDestSearch] = useState("");
  const destDropdownRef = useRef(null);

  const [showMetaDropdown, setShowMetaDropdown] = useState(false);
  const [metaSearch, setMetaSearch] = useState("");
  const metaDropdownRef = useRef(null);

  const [showFlagDropdown, setShowFlagDropdown] = useState(false);
  const [flagSearch, setFlagSearch] = useState("");
  const flagDropdownRef = useRef(null);

  const [showBadgeDropdown, setShowBadgeDropdown] = useState(false);
  const [badgeSearch, setBadgeSearch] = useState("");
  const badgeDropdownRef = useRef(null);

  const [showLoaderCheckpointDropdown, setShowLoaderCheckpointDropdown] = useState(false);
  const [loaderCheckpointSearch, setLoaderCheckpointSearch] = useState("");
  

  const [showTipDropdown, setShowTipDropdown] = useState(false);
  const [tipSearch, setTipSearch] = useState("");
  const tipDropdownRef = useRef(null);
  const normalizeId = (s) => (s || "").toLowerCase().replace(/[\s_-]/g, "");

  const [showDocDropdown, setShowDocDropdown] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  const [pendingMetaKeys, setPendingMetaKeys] = useState([]);
  const [pendingFlagLabels, setPendingFlagLabels] = useState([]);
  
  const [pendingCheckpointLabels, setPendingCheckpointLabels] = useState([]);
  const [pendingDocKeys, setPendingDocKeys] = useState([]);
  const [pendingTipTexts, setPendingTipTexts] = useState([]);
  const docDropdownRef = useRef(null);
  const loaderCheckpointDropdownRef = useRef(null);
  const [showReview, setShowReview] = useState(false);
  const [itemOverlay, setItemOverlay] = useState(null);
  const [reviewDetail, setReviewDetail] = useState(null);

  const [formShowAllMeta, setFormShowAllMeta] = useState(false);
  const [formShowAllFlags, setFormShowAllFlags] = useState(false);
  const [formShowAllCheckpoints, setFormShowAllCheckpoints] = useState(false);
  const [formShowAllDocs, setFormShowAllDocs] = useState(false);
  const [formShowAllTips, setFormShowAllTips] = useState(false);
  const FORM_INITIAL_SHOW_META = 6;
  const FORM_INITIAL_SHOW_FLAGS = 2;
  const FORM_INITIAL_SHOW_CHECKPOINTS = 4;
  const FORM_INITIAL_SHOW_DOCS_TIPS = 6;


  /* ── Get badge colors from first selected processing badge ── */
  const getBadgeColors = () => {
    if (form.processingBadges.length > 0) {
      const b = form.processingBadges[0];
      return { color: b.color, bg: b.bg, border: b.border || b.color };
    }
    return { color: "#1e3a5f", bg: "#e8f0fe", border: "#1e3a5f" };
  };

  function sortByPct(arr) {
    return [...arr].sort((a, b) => (parseFloat(a.pct) || 0) - (parseFloat(b.pct) || 0));
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (passportDropdownRef.current && !passportDropdownRef.current.contains(e.target)) {
        setShowPassportDropdown(false);
      }
      if (destDropdownRef.current && !destDropdownRef.current.contains(e.target)) {
        setShowDestDropdown(false);
      }
      if (metaDropdownRef.current && !metaDropdownRef.current.contains(e.target)) {
        setShowMetaDropdown(false);
      }
      if (flagDropdownRef.current && !flagDropdownRef.current.contains(e.target)) {
        setShowFlagDropdown(false);
      }
      if (badgeDropdownRef.current && !badgeDropdownRef.current.contains(e.target)) {
        setShowBadgeDropdown(false);
      }
      if (loaderCheckpointDropdownRef.current && !loaderCheckpointDropdownRef.current.contains(e.target)) {
        setShowLoaderCheckpointDropdown(false);
      }
      if (tipDropdownRef.current && !tipDropdownRef.current.contains(e.target)) {
        setShowTipDropdown(false);
      }
      if (docDropdownRef.current && !docDropdownRef.current.contains(e.target)) {
        setShowDocDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ── Meta handlers ── */
  const selectedMetaKeys = form.visaMeta.map((m) => m.key || m.label);


  const handleRemoveMeta = (index) => {
    setForm({ ...form, visaMeta: form.visaMeta.filter((_, i) => i !== index) });
  };

  /* ── Flag handlers ── */
  const handleRemoveFlag = (index) => {
    setForm({ ...form, visaFlags: form.visaFlags.filter((_, i) => i !== index) });
  };

  /* ── Badge handlers ── */
  const handleRemoveBadge = (index) => {
    setForm({ ...form, processingBadges: form.processingBadges.filter((_, i) => i !== index) });
  };

  /* ── Loader Checkpoint handlers ── */
  const handleRemoveLoaderCheckpoint = (index) => {
    setForm({ ...form, loaderCheckpoints: form.loaderCheckpoints.filter((_, i) => i !== index) });
  };

  /* ── Tip handlers ── */
  const handleRemoveTip = (index) => {
    setForm({ ...form, tips: form.tips.filter((_, i) => i !== index) });
  };

  /* ── Required Docs handlers ── */
  const handleRemoveDoc = (index) => {
    setForm({ ...form, documents: form.documents.filter((_, i) => i !== index) });
  };

  const [saveError, setSaveError] = useState("");

  const handleSave = () => {
    const pn = form.passportName.trim();
    const dn = form.destinationName.trim();
    if (pn && dn && pn.toLowerCase() === dn.toLowerCase()) {
      setSaveError("Passport and Destination cannot be the same country.");
      return;
    }
    setSaveError("");
    onSave({
      passportName: pn,
      destinationName: dn,
      processingTime: form.processingTime.trim(),
      visaMeta: form.visaMeta,
      visaFlags: form.visaFlags.length > 0 ? form.visaFlags : [],
      processingBadges: form.processingBadges.length > 0 ? form.processingBadges : [],
      loaderCheckpoints: sortByPct(form.loaderCheckpoints).length > 0 ? sortByPct(form.loaderCheckpoints) : [],
      tips: form.tips.length > 0 ? form.tips : [],
      documents: form.documents.length > 0 ? form.documents : [],
    });
    onClose();
  };

  const isAnyDropdownOpen = showMetaDropdown || showFlagDropdown || showBadgeDropdown || showLoaderCheckpointDropdown || showTipDropdown || showDocDropdown;
  const colors = getBadgeColors();

  const handleContinue = () => {
    setShowReview(true);
  };

  const handleBack = () => {
    setShowReview(false);
    setSaveError("");
  };

  const handleFinalSave = () => {
    handleSave();
  };

  const isFormEmpty = !form.passportName.trim() && !form.destinationName.trim();

  /* ── Item Overlay handlers ── */
  const openAddOverlay = (type) => {
    const defaults = {
      meta: { key: "", label: "", value: "" },
      flag: { label: "", text: "#000000", bg: "#ffffff", border: "#000000" },
      badge: { label: "", color: "#000000", bg: "#ffffff", border: "#000000" },
      checkpoint: { label: "", pct: "" },
      doc: { key: "", label: "", detail: "" },
      tip: { label: "", text: "" },
    };
    setItemOverlay({ type, data: defaults[type], index: null });
  };

  const openEditOverlay = (type, data, index) => {
    setItemOverlay({ type, data: { ...data }, index });
  };

  const handleOverlaySave = () => {
    if (!itemOverlay) return;
    const { type, data, index } = itemOverlay;
    const key = { meta: "visaMeta", flag: "visaFlags", badge: "processingBadges", checkpoint: "loaderCheckpoints", doc: "documents", tip: "tips" }[type];
    let updated;
    if (type === "badge" && index === null) {
      updated = [{ ...data }];
    } else {
      updated = [...form[key]];
      if (index !== null) {
        updated[index] = { ...data };
      } else {
        updated.push({ ...data });
      }
    }
    setForm({ ...form, [key]: type === "checkpoint" ? sortByPct(updated) : updated });
    setItemOverlay(null);
  };

  const closeOverlay = () => setItemOverlay(null);

  const renderOverlayFields = () => {
    if (!itemOverlay) return null;
    const { type, data } = itemOverlay;
    if (type === "meta") {
      return (
        <>
          <div>
            <label className="AdminFieldLabel">Key</label>
            <input className="AdminInput AdminInputBlock" value={data.key} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, key: e.target.value } })} placeholder="Meta key" />
          </div>
          <div>
            <label className="AdminFieldLabel">Label</label>
            <input className="AdminInput AdminInputBlock" value={data.label} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Label" />
          </div>
          <div>
            <label className="AdminFieldLabel">Value</label>
            <input className="AdminInput AdminInputBlock" value={data.value} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, value: e.target.value } })} placeholder="Value" />
          </div>
        </>
      );
    }
    if (type === "flag") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div>
              <label className="AdminFieldLabel">Display Label</label>
              <input className="AdminInput AdminInputBlock" value={data.label} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Flag label" />
            </div>
            <div>
              <label className="AdminFieldLabel">Text Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.text} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, text: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.text} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, text: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
            <div>
              <label className="AdminFieldLabel">BG Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.bg} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, bg: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.bg} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, bg: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
            <div>
              <label className="AdminFieldLabel">Border Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.border||data.text} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, border: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.border||data.text} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, border: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column"}}>
            <label className="AdminFieldLabel">Preview</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:20,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",minHeight:80,flex:1}}>
              <span style={{background:data.bg,color:data.text,border:"1px solid "+(data.border||data.text),borderRadius:6,padding:"4px 14px",fontSize:14,fontWeight:600,display:"inline-block"}}>{data.label || "Flag Label"}</span>
            </div>
          </div>
        </div>
      );
    }
    if (type === "badge") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div>
              <label className="AdminFieldLabel">Display Label</label>
              <input className="AdminInput AdminInputBlock" value={data.label} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Badge label" />
            </div>
            <div>
              <label className="AdminFieldLabel">Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.color} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, color: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.color} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, color: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
            <div>
              <label className="AdminFieldLabel">BG Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.bg} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, bg: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.bg} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, bg: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
            <div>
              <label className="AdminFieldLabel">Border Color</label>
              <div className="AdminFlexRowGap8">
                <input type="color" value={data.border||data.color} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, border: e.target.value } })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                <input className="AdminInput AdminInputBlock" value={data.border||data.color} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, border: e.target.value } })} placeholder="#hex" style={{fontFamily:"monospace"}} />
              </div>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column"}}>
            <label className="AdminFieldLabel">Preview</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:20,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",minHeight:80,flex:1}}>
              <span style={{background:data.bg,color:data.color,border:"1px solid "+(data.border||data.color),borderRadius:6,padding:"4px 14px",fontSize:14,fontWeight:600,display:"inline-block"}}>{data.label || "Badge Label"}</span>
            </div>
          </div>
        </div>
      );
    }
    if (type === "checkpoint") {
      return (
        <>
          <div>
            <label className="AdminFieldLabel">Step Label</label>
            <input className="AdminInput AdminInputBlock" value={data.label} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Step label" />
          </div>
          <div>
            <label className="AdminFieldLabel">Percentage</label>
            <input className="AdminInput AdminInputBlock" value={data.pct != null ? data.pct : ""} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, pct: e.target.value } })} placeholder="0%" type="number" min="0" max="100" />
          </div>
        </>
      );
    }
    if (type === "doc") {
      return (
        <>
          <div>
            <label className="AdminFieldLabel">Label</label>
            <input className="AdminInput AdminInputBlock" value={data.label||data.key||""} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Doc label" />
          </div>
          <div>
            <label className="AdminFieldLabel">Description</label>
            <textarea className="AdminInput AdminInputBlock" style={{resize:"vertical",minHeight:80}} value={data.detail||""} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, detail: e.target.value } })} placeholder="Description" />
          </div>
        </>
      );
    }
    if (type === "tip") {
      return (
        <>
          <div>
            <label className="AdminFieldLabel">Label</label>
            <input className="AdminInput AdminInputBlock" value={data.label||""} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, label: e.target.value } })} placeholder="Tip label" />
          </div>
          <div>
            <label className="AdminFieldLabel">Description</label>
            <textarea className="AdminInput AdminInputBlock" style={{resize:"vertical",minHeight:80}} value={data.text||data.tip_text||""} onChange={(e) => setItemOverlay({ ...itemOverlay, data: { ...data, text: e.target.value } })} placeholder="Tip description" />
          </div>
        </>
      );
    }
    return null;
  };

  const renderReviewDetail = (type, data) => {
    if (type === "meta") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div><label className="AdminFieldLabel">Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label || data.key}</div></div>
          <div><label className="AdminFieldLabel">Value</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.value || "—"}</div></div>
        </div>
      );
    }
    if (type === "flag") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div><label className="AdminFieldLabel">Display Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label}</div></div>
          <div><label className="AdminFieldLabel">Text Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.text,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.text}</div></div>
          <div><label className="AdminFieldLabel">BG Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.bg,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.bg}</div></div>
          <div><label className="AdminFieldLabel">Border Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.border||data.text,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.border||data.text}</div></div>
          <div style={{gridColumn:"1 / -1"}}>
            <label className="AdminFieldLabel">Preview</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:16,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",marginTop:4}}>
              <span className="AdminVisaRouteFlagChip" style={{background:data.bg,color:data.text,border:"2px solid "+(data.border||data.text),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{data.label}</span>
            </div>
          </div>
        </div>
      );
    }
    if (type === "badge") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div><label className="AdminFieldLabel">Display Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label}</div></div>
          <div><label className="AdminFieldLabel">Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.color,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.color}</div></div>
          <div><label className="AdminFieldLabel">BG Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.bg,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.bg}</div></div>
          <div><label className="AdminFieldLabel">Border Color</label><div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:14,color:"#1B2A4A",padding:"6px 0"}}><span style={{width:20,height:20,borderRadius:4,background:data.border||data.color,border:"1px solid #d1d5db",display:"inline-block"}}></span>{data.border||data.color}</div></div>
          <div style={{gridColumn:"1 / -1"}}>
            <label className="AdminFieldLabel">Preview</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:16,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",marginTop:4}}>
              <span className="AdminVisaRouteFlagChip" style={{background:data.bg,color:data.color,border:"2px solid "+(data.border||data.color),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{data.label}</span>
            </div>
          </div>
        </div>
      );
    }
    if (type === "checkpoint") {
      return (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div><label className="AdminFieldLabel">Step Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label}</div></div>
          <div><label className="AdminFieldLabel">Percentage</label><div style={{fontSize:24,fontWeight:700,color:"#1B2A4A",padding:"6px 0"}}>{data.pct || "0"}%</div></div>
        </div>
      );
    }
    if (type === "doc") {
      return (
        <div>
          <div><label className="AdminFieldLabel">Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label || data.key}</div></div>
          <div style={{marginTop:12}}><label className="AdminFieldLabel">Description</label><div style={{fontSize:14,color:"#64748b",lineHeight:1.5,padding:"6px 0"}}>{data.detail || "—"}</div></div>
        </div>
      );
    }
    if (type === "tip") {
      return (
        <div>
          <div><label className="AdminFieldLabel">Label</label><div style={{fontSize:14,color:"#1B2A4A",padding:"6px 0"}}>{data.label || ""}</div></div>
          <div style={{marginTop:12}}><label className="AdminFieldLabel">Description</label><div style={{fontSize:14,color:"#64748b",lineHeight:1.5,padding:"6px 0"}}>{data.text || data.tip_text || "—"}</div></div>
        </div>
      );
    }
    return null;
  };

  /* ── Review Step ── */
  function ReviewStep() {
    const [showAllDocs, setShowAllDocs] = useState(false);
    const [showAllTips, setShowAllTips] = useState(false);
    const [showAllFlags, setShowAllFlags] = useState(false);
    const [showAllMeta, setShowAllMeta] = useState(false);
    const [showAllCheckpoints, setShowAllCheckpoints] = useState(false);
    const pencilIcon = (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    );
    const INITIAL_SHOW_DOCS_TIPS = 6;
    const INITIAL_SHOW_FLAGS = 2;
    const INITIAL_SHOW_META = 6;
    const INITIAL_SHOW_CHECKPOINTS = 4;
    return (
      <div className="AdminVisaRouteReviewBody">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"start"}}>
        {/* Basic Info */}
        <div className="AdminVisaRouteReviewSection" style={{gridColumn:"1 / -1"}}>
          <div className="AdminVisaRouteReviewSectionTitle">
            Route Details
            <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px 24px"}}>
            <div className="AdminVisaRouteReviewField" style={{flexDirection:"column",alignItems:"center",textAlign:"center"}}>
              <span className="AdminVisaRouteReviewFieldLabel" style={{minWidth:0,width:"100%",textAlign:"center"}}>Passport</span>
              <span className="AdminVisaRouteReviewFieldValue" style={{flex:"none",textAlign:"center",marginTop:2}}>{form.passportName || "—"}</span>
            </div>
            <div className="AdminVisaRouteReviewField" style={{flexDirection:"column",alignItems:"center",textAlign:"center"}}>
              <span className="AdminVisaRouteReviewFieldLabel" style={{minWidth:0,width:"100%",textAlign:"center"}}>Destination</span>
              <span className="AdminVisaRouteReviewFieldValue" style={{flex:"none",textAlign:"center",marginTop:2}}>{form.destinationName || "—"}</span>
            </div>
            <div className="AdminVisaRouteReviewField" style={{flexDirection:"column",alignItems:"center",textAlign:"center"}}>
              <span className="AdminVisaRouteReviewFieldLabel" style={{minWidth:0,width:"100%",textAlign:"center"}}>Processing Time</span>
              <span className="AdminVisaRouteReviewFieldValue" style={{flex:"none",textAlign:"center",marginTop:2}}>{form.processingTime || "—"}</span>
            </div>
          </div>
        </div>

        {/* Visa Meta */}
        {form.visaMeta.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Visa Meta
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px"}}>
              {form.visaMeta.slice(0, showAllMeta ? form.visaMeta.length : INITIAL_SHOW_META).map((m, i) => (
                <div className="AdminVisaRouteReviewField" key={i} style={{cursor:"pointer",flexDirection:"column",alignItems:"center",textAlign:"center"}} onClick={() => setReviewDetail({ type:"meta", data:m, index:i })}>
                  <span className="AdminVisaRouteReviewFieldLabel" style={{minWidth:0,width:"100%",textAlign:"center"}}>{m.label || m.key}</span>
                  <span className="AdminVisaRouteReviewFieldValue" style={{flex:"none",textAlign:"center",marginTop:2}}>{m.value || "—"}</span>
                </div>
              ))}
            </div>
            {form.visaMeta.length > INITIAL_SHOW_META && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:8,fontSize:12,marginLeft:"auto",display:"block"}} onClick={() => setShowAllMeta(!showAllMeta)}>
                {showAllMeta ? "View Less" : `View More (${form.visaMeta.length - INITIAL_SHOW_META} more)`}
              </button>
            )}
          </div>
        )}

        {/* Visa Flags */}
        {form.visaFlags.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Visa Flags
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {form.visaFlags.slice(0, showAllFlags ? form.visaFlags.length : INITIAL_SHOW_FLAGS).map((f, i) => (
                <div key={i} className="AdminVisaRouteFlagItem" style={{padding:"12px 14px",cursor:"pointer"}} onClick={() => setReviewDetail({ type:"flag", data:f, index:i })}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>{f.label}</div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:12,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc"}}>
                    <span className="AdminVisaRouteFlagChip" style={{background:f.bg,color:f.text,border:"2px solid "+(f.border||f.text),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{f.label}</span>
                  </div>
                </div>
              ))}
            </div>
            {form.visaFlags.length > INITIAL_SHOW_FLAGS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:8,fontSize:12,marginLeft:"auto",display:"block"}} onClick={() => setShowAllFlags(!showAllFlags)}>
                {showAllFlags ? "View Less" : `View More (${form.visaFlags.length - INITIAL_SHOW_FLAGS} more)`}
              </button>
            )}
          </div>
        )}

        {/* Processing Badges */}
        {form.processingBadges.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Processing Badges
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            {form.processingBadges.map((b, i) => (
              <div key={i} className="AdminVisaRouteFlagItem" style={{padding:"12px 14px",cursor:"pointer"}} onClick={() => setReviewDetail({ type:"badge", data:b, index:i })}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>{b.label}</div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:12,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc"}}>
                  <span className="AdminVisaRouteFlagChip" style={{background:b.bg,color:b.color,border:"2px solid "+(b.border||b.color),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{b.label}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loader Checkpoints */}
        {form.loaderCheckpoints.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Loader Checkpoints
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {sortByPct(form.loaderCheckpoints).slice(0, showAllCheckpoints ? form.loaderCheckpoints.length : INITIAL_SHOW_CHECKPOINTS).map((lc, i) => (
                <div key={i} className="AdminVisaRouteFlagItem" style={{padding:"12px 14px",cursor:"pointer"}} onClick={() => setReviewDetail({ type:"checkpoint", data:lc, index:i })}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>{lc.label}</div>
                  <div style={{fontSize:24,fontWeight:700,color:"#1B2A4A"}}>{lc.pct || "0"}%</div>
                </div>
              ))}
            </div>
            {form.loaderCheckpoints.length > INITIAL_SHOW_CHECKPOINTS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:8,fontSize:12,marginLeft:"auto",display:"block"}} onClick={() => setShowAllCheckpoints(!showAllCheckpoints)}>
                {showAllCheckpoints ? "View Less" : `View More (${form.loaderCheckpoints.length - INITIAL_SHOW_CHECKPOINTS} more)`}
              </button>
            )}
          </div>
        )}

        {/* Required Docs */}
        {form.documents.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Required Documents
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {form.documents.slice(0, showAllDocs ? form.documents.length : INITIAL_SHOW_DOCS_TIPS).map((d, i) => (
                <div key={i} className="AdminVisaRouteFlagItem" style={{padding:"12px 14px",cursor:"pointer"}} onClick={() => setReviewDetail({ type:"doc", data:d, index:i })}>
                  <div style={{fontSize:13,fontWeight:600}}>{d.label || d.key}</div>
                </div>
              ))}
            </div>
            {form.documents.length > INITIAL_SHOW_DOCS_TIPS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:8,fontSize:12,marginLeft:"auto",display:"block"}} onClick={() => setShowAllDocs(!showAllDocs)}>
                {showAllDocs ? "View Less" : `View More (${form.documents.length - INITIAL_SHOW_DOCS_TIPS} more)`}
              </button>
            )}
          </div>
        )}

        {/* Application Tips */}
        {form.tips.length > 0 && (
          <div className="AdminVisaRouteReviewSection">
            <div className="AdminVisaRouteReviewSectionTitle">
              Application Tips
              <button type="button" onClick={() => setShowReview(false)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4}} title="Edit">{pencilIcon}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {form.tips.slice(0, showAllTips ? form.tips.length : INITIAL_SHOW_DOCS_TIPS).map((tipText, i) => {
                const tLabel = typeof tipText === "string" ? tipText : tipText.label || tipText.tip_text || tipText.text || "";
                return (
                  <div key={i} className="AdminVisaRouteFlagItem" style={{padding:"12px 14px",cursor:"pointer"}} onClick={() => setReviewDetail({ type:"tip", data:typeof tipText === "string" ? { label: tipText, text: tipText } : { label: tipText.label || tipText.tip_text || tipText.text || "", text: tipText.text || tipText.tip_text || "" }, index:i })}>
                    <div style={{fontSize:13,fontWeight:600}}>{tLabel}</div>
                  </div>
                );
              })}
            </div>
            {form.tips.length > INITIAL_SHOW_DOCS_TIPS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:8,fontSize:12,marginLeft:"auto",display:"block"}} onClick={() => setShowAllTips(!showAllTips)}>
                {showAllTips ? "View Less" : `View More (${form.tips.length - INITIAL_SHOW_DOCS_TIPS} more)`}
              </button>
            )}
          </div>
        )}

        {form.visaMeta.length === 0 && form.visaFlags.length === 0 && form.processingBadges.length === 0 && form.loaderCheckpoints.length === 0 && form.documents.length === 0 && form.tips.length === 0 && (
          <div className="AdminVisaRouteReviewSection" style={{gridColumn:"1 / -1",textAlign:"center",padding:40}}>
            <p style={{color:"#94a3b8",fontSize:14}}>No additional data configured. Fill in the form to see details here.</p>
          </div>
        )}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="AdminOverlay" onClick={onClose}>
      <div className="AdminOverlayCard AdminOverlayCardWide" onClick={(e) => e.stopPropagation()} style={{overflow:isAnyDropdownOpen?"visible":undefined}}>
        <div className="AdminOverlayHeader">
          {showReview && (
            <button type="button" onClick={handleBack} title="Back" style={{background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex",alignItems:"center",padding:4,borderRadius:4,fontSize:22}}>&larr;</button>
          )}
          <h3 style={showReview ? {marginLeft:8} : {}}>{showReview ? "" : (editingItem && editingItem.passportName ? "Edit " : "Add ")}Visa Route</h3>
          <button className="AdminIconBtn" onClick={onClose} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
        </div>

        {/* Step Indicator */}
        <div className="AdminVisaRouteSteps">
          <div className={`AdminVisaRouteStep ${!showReview ? "AdminVisaRouteStepActive" : "AdminVisaRouteStepComplete"}`}>
            <span className="AdminVisaRouteStepNum">{!showReview ? "1" : "\u2713"}</span>
            <span>Edit Details</span>
          </div>
          <div className={`AdminVisaRouteStepLine ${showReview ? "AdminVisaRouteStepLineActive" : ""}`} />
          <div className={`AdminVisaRouteStep ${showReview ? "AdminVisaRouteStepActive" : ""}`}>
            <span className="AdminVisaRouteStepNum">2</span>
            <span>Review & Confirm</span>
          </div>
        </div>

        {!showReview ? (
          <>
        <div className="AdminVisaRouteFormBody">
        <div className="AdminVisaRouteFormFields" style={{padding:"0 0 16px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          <div style={{position:"relative"}} ref={passportDropdownRef}>
            <label className="AdminFieldLabel">Passport Name</label>
            <div
              onClick={() => { setShowPassportDropdown(!showPassportDropdown); setPassportSearch(""); }}
              className="AdminVisaRouteDropdownPill"
              style={{minHeight:38,boxSizing:"border-box",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
            >
              <span style={{fontSize:13,color:form.passportName ? "#1B2A4A" : "#94a3b8",flex:1}}>
                {form.passportName || "Select passport country..."}
              </span>
              <span style={{fontSize:12,color:"#64748b"}}>▼</span>
            </div>
            {showPassportDropdown && (
              <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:9999,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
                <div style={{padding:"0 4px",marginBottom:4}}>
                  <input className="AdminInput" style={{width:"100%",boxSizing:"border-box",padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,outline:"none"}} placeholder="Search..." value={passportSearch} onChange={(e) => setPassportSearch(e.target.value)} autoFocus onClick={(e) => e.stopPropagation()} />
                </div>
                <div style={{maxHeight:180,overflowY:"auto"}}>
                  {(availableCountries || []).filter(c => !passportSearch.trim() || (c.name||"").toLowerCase().includes(passportSearch.toLowerCase()) || (c.code||"").toLowerCase().includes(passportSearch.toLowerCase())).length === 0 ? (
                    <div className="AdminVisaRouteFlagOption" style={{padding:"8px 12px",fontSize:12,color:"#94a3b8"}}>No countries found</div>
                  ) : (
                    (availableCountries || []).filter(c => !passportSearch.trim() || (c.name||"").toLowerCase().includes(passportSearch.toLowerCase()) || (c.code||"").toLowerCase().includes(passportSearch.toLowerCase())).map((c, idx) => (
                      <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); setForm({...form, passportName: c.name}); setShowPassportDropdown(false); setPassportSearch(""); }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:form.passportName === c.name ? "#eef2ff" : "transparent",borderRadius:6,padding:"6px 10px"}}>
                        <span style={{fontSize:13}}>{c.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <div style={{position:"relative"}} ref={destDropdownRef}>
            <label className="AdminFieldLabel">Destination Name</label>
            <div
              onClick={() => { setShowDestDropdown(!showDestDropdown); setDestSearch(""); }}
              className="AdminVisaRouteDropdownPill"
              style={{minHeight:38,boxSizing:"border-box",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
            >
              <span style={{fontSize:13,color:form.destinationName ? "#1B2A4A" : "#94a3b8",flex:1}}>
                {form.destinationName || "Select destination country..."}
              </span>
              <span style={{fontSize:12,color:"#64748b"}}>▼</span>
            </div>
            {showDestDropdown && (
              <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:9999,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
                <div style={{padding:"0 4px",marginBottom:4}}>
                  <input className="AdminInput" style={{width:"100%",boxSizing:"border-box",padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,outline:"none"}} placeholder="Search..." value={destSearch} onChange={(e) => setDestSearch(e.target.value)} autoFocus onClick={(e) => e.stopPropagation()} />
                </div>
                <div style={{maxHeight:180,overflowY:"auto"}}>
                  {(availableCountries || []).filter(c => !destSearch.trim() || (c.name||"").toLowerCase().includes(destSearch.toLowerCase()) || (c.code||"").toLowerCase().includes(destSearch.toLowerCase())).length === 0 ? (
                    <div className="AdminVisaRouteFlagOption" style={{padding:"8px 12px",fontSize:12,color:"#94a3b8"}}>No countries found</div>
                  ) : (
                    (availableCountries || []).filter(c => !destSearch.trim() || (c.name||"").toLowerCase().includes(destSearch.toLowerCase()) || (c.code||"").toLowerCase().includes(destSearch.toLowerCase())).map((c, idx) => (
                      <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); setForm({...form, destinationName: c.name}); setShowDestDropdown(false); setDestSearch(""); }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:form.destinationName === c.name ? "#eef2ff" : "transparent",borderRadius:6,padding:"6px 10px"}}>
                        <span style={{fontSize:13}}>{c.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="AdminFieldLabel">Processing Time</label>
            <input className="AdminInput AdminInputBlock" value={form.processingTime} onChange={(e) => setForm({...form, processingTime: e.target.value})} placeholder="e.g. 5-7 days" />
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
        {/* ── Visa Meta Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Visa Meta</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("meta")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
            <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { if (!showMetaDropdown) { setPendingMetaKeys(form.visaMeta.map(m => m.key || m.label)); setMetaSearch(""); } setShowMetaDropdown(!showMetaDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:"#94a3b8"}}>Select visa meta...</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showMetaDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={metaDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={metaSearch}
                  onChange={(e) => setMetaSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={(e) => { e.stopPropagation(); const allKeys = (availableMetas || []).map(m => m.key || m.label); const normAllKeys = allKeys.map(k => normalizeId(k)); const normPending = pendingMetaKeys.map(pk => normalizeId(pk)); if (allKeys.every(k => normPending.includes(normalizeId(k)))) { setPendingMetaKeys(pendingMetaKeys.filter(pk => !normAllKeys.includes(normalizeId(pk)))); } else { const existingNorm = new Set(pendingMetaKeys.map(pk => normalizeId(pk))); setPendingMetaKeys([...pendingMetaKeys, ...allKeys.filter(k => !existingNorm.has(normalizeId(k)))]); } }} style={{fontSize:11,whiteSpace:"nowrap",padding:"4px 10px",flexShrink:0}}>{(() => { const allKeys = (availableMetas || []).map(m => m.key || m.label); const normAllKeys = allKeys.map(k => normalizeId(k)); const pendingGridKeys = pendingMetaKeys.filter(pk => normAllKeys.includes(normalizeId(pk))); return pendingGridKeys.length > 0 && pendingGridKeys.length >= allKeys.length ? "Deselect All" : "Select All"; })()}</button>
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(availableMetas || []).filter(
                  (m) => !selectedMetaKeys.includes(m.key || m.label) ||
                    selectedMetaKeys.includes(m.key || m.label)
                ).length === 0 ? (
                  <div className="AdminVisaRouteFlagOption">No meta found</div>
                ) : (
                  (availableMetas || []).filter((m) =>
                    ((m.key || m.label || "").toLowerCase().includes(metaSearch.toLowerCase()) ||
                     (m.icon || "").toLowerCase().includes(metaSearch.toLowerCase()))
                  ).map((meta, idx) => {
                    const normPending = pendingMetaKeys.map(pk => normalizeId(pk));
                    const mk = normalizeId(meta.key);
                    const ml = normalizeId(meta.label);
                    const isSel = normPending.includes(mk) || normPending.includes(ml);
                    return (
                      <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); const allPk = pendingMetaKeys; const normPk = normPending; const k = meta.key || meta.label; const nk = normalizeId(k); const nl = normalizeId(meta.label); if (normPk.includes(nk) || normPk.includes(nl)) { const matchIdx = normPk.indexOf(nk) >= 0 ? normPk.indexOf(nk) : normPk.indexOf(nl); setPendingMetaKeys(allPk.filter((_, i) => i !== matchIdx)); } else { setPendingMetaKeys([...allPk, k]); } }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:isSel?"#eef2ff":"transparent",borderRadius:6}}>
                        <span style={{width:16,textAlign:"center",fontSize:14,color:isSel?"#4f46e5":"#94a3b8",fontWeight:700}}>{isSel ? "\u2713" : ""}</span>
                        <span>{meta.label || meta.key}</span>
                      </div>
                    );
                  })
                )}
                            </div>
<div style={{borderTop:"1px solid #e2e8f0",padding:"6px 8px",marginTop:4}}>
                  <button type="button" className="AdminButton AdminButtonSmall" style={{width:"100%",fontSize:12}} onClick={(e) => { e.stopPropagation(); const existingMetas = form.visaMeta || []; const normGridKeys = new Set((availableMetas || []).map(m => normalizeId(m.key || m.label))); const customItems = existingMetas.filter(m => !normGridKeys.has(normalizeId(m.key || m.label))); const existingMetaByNormKey = {}; existingMetas.forEach(m => { existingMetaByNormKey[normalizeId(m.key || m.label)] = m; }); const newMeta = (availableMetas || []).filter(m => pendingMetaKeys.some(pk => normalizeId(pk) === normalizeId(m.key || m.label))).map(m => { const key = m.key || m.label; const normKey = normalizeId(key); const existing = existingMetaByNormKey[normKey]; if (existing) return { ...existing, key: existing.key || key, label: m.label || key }; return { key, label: m.label || key, icon: m.icon || "", value: "" }; }); setForm({ ...form, visaMeta: [...customItems, ...newMeta] }); setShowMetaDropdown(false); setMetaSearch(""); }}>Proceed</button>
                </div>
              </div>
            )}
          </div>
          {form.visaMeta.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>{form.visaMeta.length} item(s) selected</span>
            </div>
          )}
          {form.visaMeta.length > 0 && (
            <><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {form.visaMeta.slice(0, formShowAllMeta ? form.visaMeta.length : FORM_INITIAL_SHOW_META).map((meta, idx) => (
                <div key={idx} className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer"}} onClick={() => openEditOverlay("meta", meta, idx)}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>{meta.label || meta.key}</div>
                  <div style={{fontSize:12,color:"#64748b"}}>{meta.value || ""}</div>
                  <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveMeta(idx); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
                </div>
              ))}
            </div>
            {form.visaMeta.length > FORM_INITIAL_SHOW_META && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:0,fontSize:12,marginLeft:"auto",display:"block",marginBottom:8}} onClick={() => setFormShowAllMeta(!formShowAllMeta)}>
                {formShowAllMeta ? "View Less" : `View More (${form.visaMeta.length - FORM_INITIAL_SHOW_META} more)`}
              </button>
            )}</>
          )}
        </div>

        {/* ── Visa Flags Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Visa Flags</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("flag")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
          <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { if (!showFlagDropdown) { setPendingFlagLabels(form.visaFlags.map(f => f.label)); setFlagSearch(""); } setShowFlagDropdown(!showFlagDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:"#94a3b8"}}>Select visa flags...</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showFlagDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={flagDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={flagSearch}
                  onChange={(e) => setFlagSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={(e) => { e.stopPropagation(); const allKeys = (availableFlags || []).map(f => f.label); const normAllKeys = allKeys.map(k => normalizeId(k)); const normPending = pendingFlagLabels.map(pk => normalizeId(pk)); if (allKeys.every(k => normPending.includes(normalizeId(k)))) { setPendingFlagLabels(pendingFlagLabels.filter(pk => !normAllKeys.includes(normalizeId(pk)))); } else { const existingNorm = new Set(pendingFlagLabels.map(pk => normalizeId(pk))); setPendingFlagLabels([...pendingFlagLabels, ...allKeys.filter(k => !existingNorm.has(normalizeId(k)))]); } }} style={{fontSize:11,whiteSpace:"nowrap",padding:"4px 10px",flexShrink:0}}>{(() => { const allKeys = (availableFlags || []).map(f => f.label); const normAllKeys = allKeys.map(k => normalizeId(k)); const pendingGridKeys = pendingFlagLabels.filter(pk => normAllKeys.includes(normalizeId(pk))); return pendingGridKeys.length > 0 && pendingGridKeys.length >= allKeys.length ? "Deselect All" : "Select All"; })()}</button>
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(!availableFlags || availableFlags.length === 0) ? (
                  <div className="AdminVisaRouteFlagOption">No flags found</div>
                ) : (
                  availableFlags.filter((f) =>
                    (f.label || "").toLowerCase().includes(flagSearch.toLowerCase())
                  ).map((flag, idx) => {
                    const isSel = pendingFlagLabels.some(pl => normalizeId(pl) === normalizeId(flag.label));
                    return (
                        <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); const normFlagLabel = normalizeId(flag.label); const found = pendingFlagLabels.find(pl => normalizeId(pl) === normFlagLabel); if (found) { setPendingFlagLabels(pendingFlagLabels.filter(pl => normalizeId(pl) !== normFlagLabel)); } else { setPendingFlagLabels([...pendingFlagLabels, flag.label]); } }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:isSel?"#eef2ff":"transparent",borderRadius:6}}>
                        <span style={{width:16,textAlign:"center",fontSize:14,color:isSel?"#4f46e5":"#94a3b8",fontWeight:700}}>{isSel ? "\u2713" : ""}</span>
                        <span style={{flex:1}}>{flag.label}</span>
                      </div>
                    );
                  })
                )}
                            </div>
<div style={{borderTop:"1px solid #e2e8f0",padding:"6px 8px",marginTop:4}}>
                  <button type="button" className="AdminButton AdminButtonSmall" style={{width:"100%",fontSize:12}} onClick={(e) => { e.stopPropagation(); const existingFlags = form.visaFlags || []; const normGridFlagKeys = new Set((availableFlags || []).map(f => normalizeId(f.label))); const customFlags = existingFlags.filter(f => !normGridFlagKeys.has(normalizeId(f.label))); const existingFlagByNorm = {}; existingFlags.forEach(f => { existingFlagByNorm[normalizeId(f.label)] = f; }); const newFlags = pendingFlagLabels.filter(label => normGridFlagKeys.has(normalizeId(label))).map(label => { const normLabel = normalizeId(label); const existing = existingFlagByNorm[normLabel]; if (existing) return existing; const avail = (availableFlags || []).find(f => normalizeId(f.label) === normLabel); return avail || { label, text: "#000000" }; }); setForm({ ...form, visaFlags: [...customFlags, ...newFlags] }); setShowFlagDropdown(false); setFlagSearch(""); }}>Proceed</button>
                </div>
              </div>
            )}
          </div>
          {form.visaFlags.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>{form.visaFlags.length} item(s) selected</span>
            </div>
          )}
          {form.visaFlags.length > 0 && (
            <><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {form.visaFlags.slice(0, formShowAllFlags ? form.visaFlags.length : FORM_INITIAL_SHOW_FLAGS).map((flag, idx) => (
                <div key={idx} className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer"}} onClick={() => openEditOverlay("flag", flag, idx)}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>{flag.label}</div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:12,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc"}}>
                    <span className="AdminVisaRouteFlagChip" style={{background:flag.bg,color:flag.text,border:"2px solid "+(flag.border||flag.text),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{flag.label}</span>
                  </div>
                  <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveFlag(idx); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
                </div>
              ))}
            </div>
            {form.visaFlags.length > FORM_INITIAL_SHOW_FLAGS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:0,fontSize:12,marginLeft:"auto",display:"block",marginBottom:8}} onClick={() => setFormShowAllFlags(!formShowAllFlags)}>
                {formShowAllFlags ? "View Less" : `View More (${form.visaFlags.length - FORM_INITIAL_SHOW_FLAGS} more)`}
              </button>
            )}</>
          )}
        </div>

        {/* ── Processing Badges Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Processing Badges</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("badge")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
          <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { setBadgeSearch(""); setShowBadgeDropdown(!showBadgeDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:form.processingBadges.length > 0 ? "#1B2A4A" : "#94a3b8"}}>{form.processingBadges.length > 0 ? form.processingBadges[0].label : "Select a processing badge..."}</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showBadgeDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={badgeDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={badgeSearch}
                  onChange={(e) => setBadgeSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(!availableBadges || availableBadges.length === 0) ? (
                  <div className="AdminVisaRouteFlagOption">No badges found</div>
                ) : (
                  availableBadges.filter((b) =>
                    (b.label || "").toLowerCase().includes(badgeSearch.toLowerCase())
                  ).map((badge, idx) => (
                    <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); setForm({ ...form, processingBadges: [{ label: badge.label, color: badge.color, bg: badge.bg, border: badge.border || badge.color }] }); setShowBadgeDropdown(false); setBadgeSearch(""); }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:form.processingBadges.length > 0 && form.processingBadges[0].label === badge.label ? "#eef2ff" : "transparent",borderRadius:6}}>
                      <span style={{width:16,textAlign:"center",fontSize:14,color:form.processingBadges.length > 0 && form.processingBadges[0].label === badge.label ? "#4f46e5" : "#94a3b8",fontWeight:700}}>{form.processingBadges.length > 0 && form.processingBadges[0].label === badge.label ? "\u2713" : ""}</span>
                      <span>{badge.label}</span>
                    </div>
                  ))
                )}
              </div>
              </div>
            )}
          </div>
          {form.processingBadges.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>1 item selected</span>
            </div>
          )}
          {form.processingBadges.length > 0 && (() => {
            const badge = form.processingBadges[0];
            return (
              <div className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer",marginBottom:8}} onClick={() => openEditOverlay("badge", badge, 0)}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>{badge.label}</div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:12,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc"}}>
                  <span className="AdminVisaRouteFlagChip" style={{background:badge.bg,color:badge.color,border:"2px solid "+(badge.border||badge.color),borderRadius:6,padding:"6px 20px",fontSize:14,fontWeight:600,display:"inline-block"}}>{badge.label}</span>
                </div>
                <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveBadge(0); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
              </div>
            );
          })()}
        </div>

        {/* ── Loader Checkpoints Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Loader Checkpoints</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("checkpoint")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
          <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { if (!showLoaderCheckpointDropdown) { setPendingCheckpointLabels(form.loaderCheckpoints.map(lc => lc.label)); setLoaderCheckpointSearch(""); } setShowLoaderCheckpointDropdown(!showLoaderCheckpointDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:"#94a3b8"}}>Select loader checkpoints...</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showLoaderCheckpointDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={loaderCheckpointDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={loaderCheckpointSearch}
                  onChange={(e) => setLoaderCheckpointSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={(e) => { e.stopPropagation(); const allKeys = (availableLoaderCheckpoints || []).map(lc => lc.label || lc.key || ""); const normAllKeys = allKeys.map(k => normalizeId(k)); const normPending = pendingCheckpointLabels.map(pk => normalizeId(pk)); if (allKeys.every(k => normPending.includes(normalizeId(k)))) { setPendingCheckpointLabels(pendingCheckpointLabels.filter(pk => !normAllKeys.includes(normalizeId(pk)))); } else { const existingNorm = new Set(pendingCheckpointLabels.map(pk => normalizeId(pk))); setPendingCheckpointLabels([...pendingCheckpointLabels, ...allKeys.filter(k => !existingNorm.has(normalizeId(k)))]); } }} style={{fontSize:11,whiteSpace:"nowrap",padding:"4px 10px",flexShrink:0}}>{(() => { const allKeys = (availableLoaderCheckpoints || []).map(lc => lc.label || lc.key || ""); const normAllKeys = allKeys.map(k => normalizeId(k)); const pendingGridKeys = pendingCheckpointLabels.filter(pk => normAllKeys.includes(normalizeId(pk))); return pendingGridKeys.length > 0 && pendingGridKeys.length >= allKeys.length ? "Deselect All" : "Select All"; })()}</button>
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(!availableLoaderCheckpoints || availableLoaderCheckpoints.length === 0) ? (
                  <div className="AdminVisaRouteFlagOption">No checkpoints available</div>
                ) : (
                  availableLoaderCheckpoints
                    .filter((lc) => (lc.label || lc.key || "").toLowerCase().includes(loaderCheckpointSearch.toLowerCase()))
                    .map((lc, idx) => {
                      const isSel = pendingCheckpointLabels.some(pk => normalizeId(pk) === normalizeId(lc.label || lc.key || ""));
                      return (
                        <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); const lcKey = lc.label || lc.key || ""; const normLcKey = normalizeId(lcKey); const found = pendingCheckpointLabels.find(pk => normalizeId(pk) === normLcKey); if (found) { setPendingCheckpointLabels(pendingCheckpointLabels.filter(pl => normalizeId(pl) !== normLcKey)); } else { setPendingCheckpointLabels([...pendingCheckpointLabels, lcKey]); } }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:isSel?"#eef2ff":"transparent",borderRadius:6}}>
                          <span style={{width:16,textAlign:"center",fontSize:14,color:isSel?"#4f46e5":"#94a3b8",fontWeight:700}}>{isSel ? "\u2713" : ""}</span>
                          <span style={{fontSize:13,fontWeight:600}}>{lc.label || lc.key}</span>
                        </div>
                      );
                    })
                )}
                            </div>
<div style={{borderTop:"1px solid #e2e8f0",padding:"6px 8px",marginTop:4}}>
                  <button type="button" className="AdminButton AdminButtonSmall" style={{width:"100%",fontSize:12}} onClick={(e) => { e.stopPropagation(); const existingLCs = form.loaderCheckpoints || []; const normGridLCKeys = new Set((availableLoaderCheckpoints || []).map(lc => normalizeId(lc.label || lc.key || ""))); const customLCs = existingLCs.filter(lc => !normGridLCKeys.has(normalizeId(lc.label || lc.key || ""))); const existingLCByNorm = {}; existingLCs.forEach(lc => { existingLCByNorm[normalizeId(lc.label || lc.key || "")] = lc; }); const newLCs = pendingCheckpointLabels.filter(label => normGridLCKeys.has(normalizeId(label))).map(label => { const normLabel = normalizeId(label); const existing = existingLCByNorm[normLabel]; if (existing) return existing; const clrs = getBadgeColors(); const avail = (availableLoaderCheckpoints || []).find(lc => normalizeId(lc.label || lc.key || "") === normLabel); return avail ? { label: avail.label || avail.key || label, pct: avail.pct != null ? avail.pct : "", color: clrs.color, bg: clrs.bg, border: clrs.border } : { label, pct: "", color: clrs.color, bg: clrs.bg, border: clrs.border }; }); setForm({ ...form, loaderCheckpoints: [...customLCs, ...newLCs] }); setShowLoaderCheckpointDropdown(false); setLoaderCheckpointSearch(""); }}>Proceed</button>
                </div>
              </div>
            )}
          </div>
          <div style={{marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8}}>
              <label style={{fontSize:12,fontWeight:600,color:"#64748b",whiteSpace:"nowrap"}}>Bar Color:</label>
              <span style={{width:28,height:28,borderRadius:6,background:colors.color,border:"1px solid #d1d5db",display:"inline-block",flexShrink:0}}></span>
              <span style={{fontSize:12,fontFamily:"monospace",color:"#1B2A4A",fontWeight:600}}>{colors.color}</span>
              <span style={{fontSize:11,color:"#94a3b8",marginLeft:"auto"}}>(from badge)</span>
            </div>
          </div>
          {form.loaderCheckpoints.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>{form.loaderCheckpoints.length} item(s) selected</span>
            </div>
          )}
          {form.loaderCheckpoints.length > 0 && (
            <><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {sortByPct(form.loaderCheckpoints).slice(0, formShowAllCheckpoints ? form.loaderCheckpoints.length : FORM_INITIAL_SHOW_CHECKPOINTS).map((lc, idx) => (
                <div key={idx} className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer"}} onClick={() => openEditOverlay("checkpoint", lc, idx)}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>{lc.label}</div>
                  <div style={{fontSize:24,fontWeight:700,color:"#1B2A4A"}}>{lc.pct || "0"}%</div>
                  <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveLoaderCheckpoint(idx); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
                </div>
              ))}
            </div>
            {form.loaderCheckpoints.length > FORM_INITIAL_SHOW_CHECKPOINTS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:0,fontSize:12,marginLeft:"auto",display:"block",marginBottom:8}} onClick={() => setFormShowAllCheckpoints(!formShowAllCheckpoints)}>
                {formShowAllCheckpoints ? "View Less" : `View More (${form.loaderCheckpoints.length - FORM_INITIAL_SHOW_CHECKPOINTS} more)`}
              </button>
            )}</>
          )}
        </div>

        {/* ── Required Docs Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Required Docs</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("doc")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
          <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { if (!showDocDropdown) { setPendingDocKeys(form.documents.map(d => d.label || d.key || "")); setDocSearch(""); } setShowDocDropdown(!showDocDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:"#94a3b8"}}>Select required docs...</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showDocDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={docDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={(e) => { e.stopPropagation(); const allLabels = (availableRequiredDocs || []).map(d => d.label || d.key || ""); const normAllLabels = allLabels.map(k => normalizeId(k)); const normPending = pendingDocKeys.map(pk => normalizeId(pk)); if (allLabels.every(k => normPending.includes(normalizeId(k)))) { setPendingDocKeys(pendingDocKeys.filter(pk => !normAllLabels.includes(normalizeId(pk)))); } else { const existingNorm = new Set(pendingDocKeys.map(pk => normalizeId(pk))); setPendingDocKeys([...pendingDocKeys, ...allLabels.filter(k => !existingNorm.has(normalizeId(k)))]); } }} style={{fontSize:11,whiteSpace:"nowrap",padding:"4px 10px",flexShrink:0}}>{(() => { const allLabels = (availableRequiredDocs || []).map(d => d.label || d.key || ""); const normAllLabels = allLabels.map(k => normalizeId(k)); const pendingGridKeys = pendingDocKeys.filter(pk => normAllLabels.includes(normalizeId(pk))); return pendingGridKeys.length > 0 && pendingGridKeys.length >= allLabels.length ? "Deselect All" : "Select All"; })()}</button>
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(!availableRequiredDocs || availableRequiredDocs.length === 0) ? (
                  <div className="AdminVisaRouteFlagOption">No docs available</div>
                ) : (
                  availableRequiredDocs
                    .filter((doc) =>
                      ((doc.label || doc.key || "").toLowerCase().includes(docSearch.toLowerCase()) ||
                       (doc.key || "").toLowerCase().includes(docSearch.toLowerCase()))
                    )
                    .map((doc, idx) => {
                      const docLabel = doc.label || doc.key || "";
                      const isSel = pendingDocKeys.some(pk => normalizeId(pk) === normalizeId(docLabel));
                      return (
                        <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); const normDocLabel = normalizeId(docLabel); const found = pendingDocKeys.find(pk => normalizeId(pk) === normDocLabel); if (found) { setPendingDocKeys(pendingDocKeys.filter(pk => normalizeId(pk) !== normDocLabel)); } else { setPendingDocKeys([...pendingDocKeys, docLabel]); } }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:isSel?"#eef2ff":"transparent",borderRadius:6}}>
                          <span style={{width:16,textAlign:"center",fontSize:14,color:isSel?"#4f46e5":"#94a3b8",fontWeight:700}}>{isSel ? "\u2713" : ""}</span>
                          <span style={{fontSize:13}}>{doc.label || doc.key}: {doc.description || ""}</span>
                        </div>
                      );
                    })
                )}
                            </div>
<div style={{borderTop:"1px solid #e2e8f0",padding:"6px 8px",marginTop:4}}>
                  <button type="button" className="AdminButton AdminButtonSmall" style={{width:"100%",fontSize:12}} onClick={(e) => { e.stopPropagation(); const existingDocs = form.documents || []; const normGridDocKeys = new Set((availableRequiredDocs || []).map(d => normalizeId(d.label || d.key || ""))); const customDocs = existingDocs.filter(d => !normGridDocKeys.has(normalizeId(d.label || d.key || ""))); const existingDocByNorm = {}; existingDocs.forEach(d => { existingDocByNorm[normalizeId(d.label || d.key || "")] = d; }); const newDocs = pendingDocKeys.filter(label => normGridDocKeys.has(normalizeId(label))).map(label => { const normLabel = normalizeId(label); const existing = existingDocByNorm[normLabel]; if (existing) return existing; const avail = (availableRequiredDocs || []).find(d => normalizeId(d.label || d.key || "") === normLabel); return avail || { key: label, label, description: "" }; }); setForm({ ...form, documents: [...customDocs, ...newDocs] }); setShowDocDropdown(false); setDocSearch(""); }}>Proceed</button>
                </div>
              </div>
            )}
          </div>
          {form.documents.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>{form.documents.length} item(s) selected</span>
            </div>
          )}
          {form.documents.length > 0 && (
            <><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {form.documents.slice(0, formShowAllDocs ? form.documents.length : FORM_INITIAL_SHOW_DOCS_TIPS).map((doc, idx) => (
                <div key={idx} className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer"}} onClick={() => openEditOverlay("doc", doc, idx)}>
                  <div style={{fontSize:13,fontWeight:600}}>{doc.label || doc.key}</div>
                  <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveDoc(idx); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
                </div>
              ))}
            </div>
            {form.documents.length > FORM_INITIAL_SHOW_DOCS_TIPS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:0,fontSize:12,marginLeft:"auto",display:"block",marginBottom:8}} onClick={() => setFormShowAllDocs(!formShowAllDocs)}>
                {formShowAllDocs ? "View Less" : `View More (${form.documents.length - FORM_INITIAL_SHOW_DOCS_TIPS} more)`}
              </button>
            )}</>
          )}
        </div>

        {/* ── Application Tips Section ── */}
        <div className="AdminVisaRouteMetaSection" style={{padding:"0 20px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span className="AdminSectionTitle" style={{fontSize:15}}>Application Tips</span>
            <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openAddOverlay("tip")} style={{marginLeft:"auto",fontSize:12,marginTop:4}}>+ Add</button>
          </div>
          <div style={{position:"relative",marginBottom:8}}>
            <div
              onClick={() => { if (!showTipDropdown) { setPendingTipTexts(form.tips.map(t => typeof t === "string" ? t : t.text || "")); setTipSearch(""); } setShowTipDropdown(!showTipDropdown); }}
              className="AdminVisaRouteDropdownPill"
            >
              <span style={{fontSize:13,color:"#94a3b8"}}>Select application tips...</span>
              <span style={{marginLeft:"auto",fontSize:12,color:"#64748b"}}>▶</span>
            </div>
            {showTipDropdown && (
              <div className="AdminVisaRouteFlagDropdown" ref={tipDropdownRef} style={{zIndex:9999,position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",padding:4}}>
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px"}}>
                <input
                  className="AdminInput"
                  style={{flex:1,minWidth:0,margin:0,padding:"6px 10px",fontSize:12,border:"1px solid #d1d5db",borderRadius:9999,boxSizing:"border-box",outline:"none"}}
                  placeholder="Search..."
                  value={tipSearch}
                  onChange={(e) => setTipSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={(e) => { e.stopPropagation(); const allKeys = (availableTips || []).map(tip => tip.tip_text); const normAllKeys = allKeys.map(k => normalizeId(k)); const normPending = pendingTipTexts.map(pk => normalizeId(pk)); if (allKeys.every(k => normPending.includes(normalizeId(k)))) { setPendingTipTexts(pendingTipTexts.filter(pk => !normAllKeys.includes(normalizeId(pk)))); } else { const existingNorm = new Set(pendingTipTexts.map(pk => normalizeId(pk))); setPendingTipTexts([...pendingTipTexts, ...allKeys.filter(k => !existingNorm.has(normalizeId(k)))]); } }} style={{fontSize:11,whiteSpace:"nowrap",padding:"4px 10px",flexShrink:0}}>{(() => { const allKeys = (availableTips || []).map(tip => tip.tip_text); const normAllKeys = allKeys.map(k => normalizeId(k)); const pendingGridKeys = pendingTipTexts.filter(pk => normAllKeys.includes(normalizeId(pk))); return pendingGridKeys.length > 0 && pendingGridKeys.length >= allKeys.length ? "Deselect All" : "Select All"; })()}</button>
              </div>
              <div style={{maxHeight:150,overflowY:"auto"}}>
                {(!availableTips || availableTips.length === 0) ? (
                  <div className="AdminVisaRouteFlagOption">No tips available</div>
                ) : (
                  availableTips
                    .filter((tip) =>
                      (tip.tip_text || "").toLowerCase().includes(tipSearch.toLowerCase())
                    )
                    .map((tip, idx) => {
                      const isSel = pendingTipTexts.some(pt => normalizeId(pt) === normalizeId(tip.tip_text));
                      return (
                        <div key={idx} className="AdminVisaRouteFlagOption" onClick={(e) => { e.stopPropagation(); const normTipText = normalizeId(tip.tip_text); const found = pendingTipTexts.find(pt => normalizeId(pt) === normTipText); if (found) { setPendingTipTexts(pendingTipTexts.filter(pt => normalizeId(pt) !== normTipText)); } else { setPendingTipTexts([...pendingTipTexts, tip.tip_text]); } }} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:isSel?"#eef2ff":"transparent",borderRadius:6}}>
                          <span style={{width:16,textAlign:"center",fontSize:14,color:isSel?"#4f46e5":"#94a3b8",fontWeight:700}}>{isSel ? "\u2713" : ""}</span>
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            <span style={{fontSize:13,fontWeight:600,color:"#1B2A4A"}}>{tip.label || tip.tip_text}</span>
                            <span style={{fontSize:11,color:"#64748b"}}>{tip.tip_text}</span>
                          </div>
                        </div>
                      );
                    })
                )}
                            </div>
<div style={{borderTop:"1px solid #e2e8f0",padding:"6px 8px",marginTop:4}}>
                  <button type="button" className="AdminButton AdminButtonSmall" style={{width:"100%",fontSize:12}} onClick={(e) => { e.stopPropagation(); const existingTips = form.tips || []; const normGridTipKeys = new Set((availableTips || []).map(t => normalizeId(t.tip_text))); const customTips = existingTips.filter(t => { const tKey = typeof t === "string" ? t : t.tip_text || t.text || t.label || ""; return !normGridTipKeys.has(normalizeId(tKey)); }); const existingTipByNorm = {}; existingTips.forEach(t => { const tKey = typeof t === "string" ? t : t.tip_text || t.text || t.label || ""; existingTipByNorm[normalizeId(tKey)] = t; }); const newTips = pendingTipTexts.filter(text => normGridTipKeys.has(normalizeId(text))).map(text => { const normText = normalizeId(text); const existing = existingTipByNorm[normText]; if (existing) return existing; const avail = (availableTips || []).find(t => normalizeId(t.tip_text) === normText); return avail ? { label: avail.label || avail.tip_text, text: avail.tip_text } : { label: text, text }; }); setForm({ ...form, tips: [...customTips, ...newTips] }); setShowTipDropdown(false); setTipSearch(""); }}>Proceed</button>
                </div>
              </div>
            )}
          </div>
          {form.tips.length > 0 && (
            <div className="AdminVisaRouteSelectedCount">
              <span style={{fontSize:12,color:"#64748b"}}>{form.tips.length} item(s) selected</span>
            </div>
          )}
          {form.tips.length > 0 && (
            <><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {form.tips.slice(0, formShowAllTips ? form.tips.length : FORM_INITIAL_SHOW_DOCS_TIPS).map((tipText, idx) => {
                const tipLabel = typeof tipText === "string" ? tipText : tipText.label || tipText.tip_text || tipText.text || "";
                return (
                  <div key={idx} className="AdminVisaRouteFlagItem" style={{position:"relative",padding:"12px 14px",cursor:"pointer"}} onClick={() => openEditOverlay("tip", typeof tipText === "string" ? { label: tipText, text: tipText } : { label: tipText.label || tipText.tip_text || tipText.text || "", text: tipText.text || tipText.tip_text || "" }, idx)}>
                    <div style={{fontSize:13,fontWeight:600}}>{tipLabel}</div>
                    <button className="AdminVisaRouteSelectedItemRemove" onClick={(e) => { e.stopPropagation(); handleRemoveTip(idx); }} title="Remove" style={{position:"absolute",top:8,right:8,width:22,height:22}}>&times;</button>
                  </div>
                );
              })}
            </div>
            {form.tips.length > FORM_INITIAL_SHOW_DOCS_TIPS && (
              <button type="button" className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{marginTop:0,fontSize:12,marginLeft:"auto",display:"block",marginBottom:8}} onClick={() => setFormShowAllTips(!formShowAllTips)}>
                {formShowAllTips ? "View Less" : `View More (${form.tips.length - FORM_INITIAL_SHOW_DOCS_TIPS} more)`}
              </button>
            )}</>
          )}
        </div>
        </div>
        </div>
        <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
          <button
            className="AdminButton"
            style={{width:140}}
            onClick={handleContinue}
            disabled={isFormEmpty}
          >
            Continue
          </button>
        </div>
          </>
        ) : (
          <>
         <ReviewStep />
        {saveError && (
          <div style={{color:"#dc2626",textAlign:"center",margin:"8px 0",fontSize:14}}>
            {saveError}
          </div>
        )}
        <div className="AdminOverlayFooter" style={{justifyContent:"center",gap:12}}>
          <button
            className="AdminButton"
            style={{width:140}}
            onClick={handleFinalSave}
            disabled={isFormEmpty}
          >
            {editingItem && editingItem.passportName ? "Save" : "Add"} Route
          </button>
        </div>
          </>
        )}
      </div>
    </div>
    {reviewDetail && (() => {
      const { type, data } = reviewDetail;
      const pencilIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      );
      const sectionLabels = { meta:"Visa Meta", flag:"Visa Flag", badge:"Processing Badge", checkpoint:"Loader Checkpoint", doc:"Required Doc", tip:"Application Tip" };
      return (
        <div className="AdminOverlay" onClick={() => setReviewDetail(null)}>
          <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()} style={{maxWidth:520}}>
            <div className="AdminOverlayHeader">
              <h3>{sectionLabels[type]}</h3>
              <button className="AdminIconBtn" onClick={() => setReviewDetail(null)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"flex",flexDirection:"column",gap:16}}>
              {renderReviewDetail(type, data)}
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center",gap:12}}>
              <button className="AdminButton" style={{display:"inline-flex",alignItems:"center",gap:6}} onClick={() => { setReviewDetail(null); setShowReview(false); }}>
                {pencilIcon} Edit
              </button>
            </div>
          </div>
        </div>
      );
    })()}
    {itemOverlay && (() => {
      const { type, data } = itemOverlay;
      const isAdd = itemOverlay.index === null;
      const sectionLabels = { meta:"Visa Meta", flag:"Visa Flag", badge:"Processing Badge", checkpoint:"Loader Checkpoint", doc:"Required Doc", tip:"Application Tip" };
      return (
        <div className="AdminOverlay" onClick={closeOverlay}>
          <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()} style={{maxWidth:560}}>
            <div className="AdminOverlayHeader">
              <h3>{isAdd ? "Add" : "Edit"} {sectionLabels[type]}</h3>
              <button className="AdminIconBtn" onClick={closeOverlay} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"flex",flexDirection:"column",gap:16}}>
              {renderOverlayFields()}
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
              <button className="AdminButton" style={{width:100}}
                onClick={handleOverlaySave}
                disabled={(type==="meta" && !data.label.trim()) || (type==="flag" && !data.label.trim()) || (type==="badge" && !data.label.trim()) || (type==="checkpoint" && !data.label.trim()) || (type==="doc" && !data.key.trim() && !data.label.trim()) || (type==="tip" && !data.text.trim() && !data.tip_text?.trim())}
              >{isAdd ? "Add" : "Save"}</button>
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
}

function AdminRequirementsEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("visaMetaGrid");
  const [tabOrder, setTabOrder] = useState(REQUIREMENTS_TABS.map(t => t.id));
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const searchRef = useRef(null);
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  const [needsPublish, setNeedsPublish] = useState(false);
  const [showRowForm, setShowRowForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState({});
  const [pendingRowInsert, setPendingRowInsert] = useState(null);
  const [visaRouteFormOpen, setVisaRouteFormOpen] = useState(false);
  const [visaRouteEditingItem, setVisaRouteEditingItem] = useState(null);
  const [visaRouteEditIndex, setVisaRouteEditIndex] = useState(null);
  const [visaRoutePendingInsert, setVisaRoutePendingInsert] = useState(null);
  const [visaRoutes, setVisaRoutes] = useState([]);
  const [visaRoutesLoading, setVisaRoutesLoading] = useState(false);
  const [visaRoutesError, setVisaRoutesError] = useState("");
  const [dbCountries, setDbCountries] = useState([]);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const [tips, setTips] = useState([]);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [tipsError, setTipsError] = useState("");
  const [tipSearch, setTipSearch] = useState("");
  const tipSearchRef = useRef(null);
  const [editingTipOverlay, setEditingTipOverlay] = useState(null);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
  }, [activeTab]);

  const fetchVisaRoutes = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setVisaRoutesLoading(true);
    setVisaRoutesError("");
    try {
      const res = await fetch(`${API_BASE_URL}/visa-routes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.routes) {
          const mapped = data.routes.map((r) => {
            const config = typeof r.config === "string" ? JSON.parse(r.config || "{}") : (r.config || {});
            return {
              id: r.id,
              passportName: r.passport_name,
              destinationName: r.destination_name,
              passportCode: r.passport_code,
              destinationCode: r.destination_code,
              processingTime: r.processing_time,
              ...config,
            };
          });
          setVisaRoutes(mapped);
        }
      } else {
        setVisaRoutesError("Failed to load visa routes.");
      }
    } catch (err) {
      setVisaRoutesError("Failed to load visa routes: " + err.message);
    } finally {
      setVisaRoutesLoading(false);
    }
  };

  const fetchDbCountries = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.countries) {
          setDbCountries(data.countries);
          return data.countries;
        }
      }
    } catch (err) {
      // silent
    }
    return [];
  };

  const handleVisaRouteDelete = async (index) => {
    const route = visaRoutes[index];
    if (!route || !route.id) return;
    if (!window.confirm(`Delete route: ${route.passportName} → ${route.destinationName}?`)) return;
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/visa-routes/${route.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchVisaRoutes();
        setNeedsPublish(true);
      } else {
        setVisaRoutesError("Failed to delete route.");
      }
    } catch (err) {
      setVisaRoutesError("Failed to delete route.");
    }
  };

  const handleVisaRouteReorder = async (index, direction) => {
    const newRoutes = [...visaRoutes];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newRoutes.length) return;
    const token = sessionStorage.getItem("tvc_token");
    [newRoutes[index], newRoutes[swapIndex]] = [newRoutes[swapIndex], newRoutes[index]];
    const reorderedIds = newRoutes.filter((r) => r.id).map((r) => r.id);
    try {
      const res = await fetch(`${API_BASE_URL}/visa-routes/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: reorderedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.routes) {
          const mapped = data.routes.map((r) => {
            const config = typeof r.config === "string" ? JSON.parse(r.config || "{}") : (r.config || {});
            return {
              id: r.id,
              passportName: r.passport_name,
              destinationName: r.destination_name,
              passportCode: r.passport_code,
              destinationCode: r.destination_code,
              processingTime: r.processing_time,
              ...config,
            };
          });
          setVisaRoutes(mapped);
          setNeedsPublish(true);
        }
      }
    } catch (err) {
      setVisaRoutesError("Failed to reorder routes.");
    }
  };

  const handleVisaRouteSave = async (routeData) => {
    const isEdit = visaRouteEditIndex !== null;
    // Snapshot original state in case we need to revert on error
    const originalRoutes = [...visaRoutes];
    // Optimistic local update — shows immediately in the route table
    const updatedRoutes = [...visaRoutes];
    if (isEdit) {
      updatedRoutes[visaRouteEditIndex] = { ...updatedRoutes[visaRouteEditIndex], ...routeData };
    } else {
      const insertAt = visaRoutePendingInsert ?? updatedRoutes.length;
      updatedRoutes.splice(insertAt, 0, routeData);
    }
    setVisaRoutes(updatedRoutes);
    setContent(prev => {
      const routes = [...(prev.visaRoutes || [])];
      if (isEdit && visaRouteEditIndex !== null && visaRouteEditIndex < routes.length) {
        routes[visaRouteEditIndex] = { ...(routes[visaRouteEditIndex] || {}), ...routeData };
      } else {
        routes.splice(visaRoutePendingInsert ?? routes.length, 0, routeData);
      }
      return { ...prev, visaRoutes: routes };
    });
    setNeedsPublish(true);
    setVisaRouteFormOpen(false);
        // Persist to backend in the background
    try {
      const token = sessionStorage.getItem("tvc_token");
      const routeId = isEdit ? visaRoutes[visaRouteEditIndex]?.id : null;
      // Translate country names to DB-matching names so the backend lookup succeeds
      const findDbName = (name) => {
        if (!name || dbCountries.length === 0) return name;
        const match = dbCountries.find(c => c.name.toLowerCase() === name.toLowerCase());
        return match ? match.name : name;
      };
      const backendData = {
        ...routeData,
        passportName: findDbName(routeData.passportName),
        destinationName: findDbName(routeData.destinationName),
      };
      const url = routeId
        ? `${API_BASE_URL}/visa-routes/${routeId}`
        : `${API_BASE_URL}/visa-routes`;
      const body = routeId
        ? JSON.stringify(backendData)
        : JSON.stringify({ ...backendData, insertAt: visaRoutePendingInsert });
      const res = await fetch(url, {
        method: routeId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body,
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => "Unknown error");
        console.error("Route save API error:", res.status, errBody);
        console.error("  routeData:", routeData);
        console.error("  backendData:", backendData);
        console.error("  dbCountries:", dbCountries);
        setMessage(`Failed to save route (${res.status}): ${errBody.substring(0, 200)}`);
        // Revert optimistic update
        setVisaRoutes(originalRoutes);
      } else {
        const saved = await res.json();
        if (saved && saved.route) {
          const sr = saved.route;
          const config = typeof sr.config === "string" ? JSON.parse(sr.config || "{}") : (sr.config || {});
          const serverItem = {
            id: sr.id,
            passportName: routeData.passportName,
            destinationName: routeData.destinationName,
            passportCode: sr.passport_code,
            destinationCode: sr.destination_code,
            processingTime: sr.processing_time,
            ...config,
          };
          setVisaRoutes(prev => {
            if (isEdit && visaRouteEditIndex !== null && visaRouteEditIndex < prev.length) {
              const next = [...prev];
              next[visaRouteEditIndex] = serverItem;
              return next;
            }
            const insertAt = visaRoutePendingInsert ?? prev.length;
            const next = [...prev];
            if (insertAt <= prev.length) {
              next.splice(insertAt, 0, serverItem);
            } else {
              next.push(serverItem);
            }
            return next;
          });
        }
      }
    } catch (err) {
      console.error("Background route save failed:", err);
      setMessage(`Network error saving route: ${err.message}. Your changes may not be visible on the public site.`);
    }
  };

  useEffect(() => {
    if (activeTab === "applicationTips" || activeTab === "visaRoutes") {
      fetchTips();
    }
  }, [activeTab]);

  const fetchTips = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setTipsLoading(true);
    setTipsError("");
    try {
      const res = await fetch(`${API_BASE_URL}/application-tips`);
      const data = await res.json();
      if (data && data.tips) {
        setTips(data.tips);
      }
    } catch (err) {
      setTipsError("Failed to load tips.");
    } finally {
      setTipsLoading(false);
    }
  };

  const handleAddTip = async () => {
    setEditingTipOverlay({ id: null, label: "Tip " + (tips.length + 1), text: "" });
  };

  const handleMoveTip = async (index, direction) => {
    const newTips = [...tips];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newTips.length) return;
    [newTips[index], newTips[swapIndex]] = [newTips[swapIndex], newTips[index]];
    const reorderedIds = newTips.map((t) => t.id);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/application-tips/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: reorderedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.tips) setTips(data.tips);
        setNeedsPublish(true);
      }
    } catch (err) {
      setTipsError("Failed to reorder tips.");
    }
  };

  const handleAddTipAt = async (index, offset) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/application-tips`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label: "Tip " + (tips.length + 1), tip_text: "New tip" }),
      });
      if (res.ok) {
        const data = await res.json();
        const newTip = data.tip || data;
        const newTips = [...tips];
        const insertAt = Math.min(index + offset, newTips.length);
        newTips.splice(insertAt, 0, newTip);
        const reorderedIds = newTips.map((t) => t.id);
        const reorderRes = await fetch(`${API_BASE_URL}/application-tips/reorder`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ order: reorderedIds }),
        });
        if (reorderRes.ok) {
          const reorderData = await reorderRes.json();
          if (reorderData && reorderData.tips) setTips(reorderData.tips);
            setNeedsPublish(true);
        }
      }
    } catch (err) {
      setTipsError("Failed to add tip.");
    }
  };

  const handleDeleteTip = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      await fetch(`${API_BASE_URL}/application-tips/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchTips();
      setNeedsPublish(true);
    } catch (err) {
      setTipsError("Failed to delete tip.");
    }
  };

  const handleStartEdit = (tip) => {
    setEditingTipOverlay({ id: tip.id, label: tip.label || "", text: tip.tip_text });
  };

  const handleSaveEdit = async (id) => {
    if (!editingTipOverlay?.text.trim()) return;
    const token = sessionStorage.getItem("tvc_token");
    const insertAt = editingTipOverlay._insertAt;
    try {
      if (id) {
        await fetch(`${API_BASE_URL}/application-tips/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ label: editingTipOverlay.label || "", tip_text: editingTipOverlay.text.trim() }),
        });
        await fetchTips();
      } else {
        const res = await fetch(`${API_BASE_URL}/application-tips`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ label: editingTipOverlay.label || "", tip_text: editingTipOverlay.text.trim() }),
        });
        if (!res.ok) throw new Error("Failed to add tip");
        if (insertAt !== undefined) {
          const data = await res.json();
          const newTip = data.tip || data;
          const newTips = [...tips];
          newTips.splice(insertAt, 0, newTip);
          const reorderedIds = newTips.map((t) => t.id);
          const reorderRes = await fetch(`${API_BASE_URL}/application-tips/reorder`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ order: reorderedIds }),
          });
          if (reorderRes.ok) {
            const reorderData = await reorderRes.json();
            if (reorderData && reorderData.tips) setTips(reorderData.tips);
            setNeedsPublish(true);
          }
        } else {
          await fetchTips();
        }
      }
      setNeedsPublish(true);
    } catch (err) {
      setTipsError("Failed to save tip.");
    }
    setEditingTipOverlay(null);
  };

  const [badges, setBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [badgesError, setBadgesError] = useState("");

  useEffect(() => {
    if (activeTab === "processingBadges" || activeTab === "visaRoutes") {
      fetchBadges();
    }
    if (activeTab === "visaRoutes") {
      fetchVisaRoutes();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "visaRoutes") {
      fetchDbCountries();
    }
  }, [activeTab]);

  const fetchBadges = async () => {
    setBadgesLoading(true);
    setBadgesError("");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges`);
      const data = await res.json();
      if (data && data.badges) setBadges(data.badges);
    } catch {
      setBadgesError("Failed to load badges.");
    } finally {
      setBadgesLoading(false);
    }
  };

  const handleUpdateBadge = async (id, label, color, bg, border) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label, color, bg, border }),
      });
      if (res.ok) { await fetchBadges(); setNeedsPublish(true); }
    } catch {
      setBadgesError("Failed to update badge.");
    }
  };

  const handleDeleteBadge = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { await fetchBadges(); setNeedsPublish(true); }
    } catch {
      setBadgesError("Failed to delete badge.");
    }
  };

  const handleAddBadge = (insertAt) => {
    setEditingBadge({ isNew: true, insertAt, id: null, label: "New Band", color: "#000000", bg: "#ffffff", border: "#000000" });
  };

  const handleCreateBadge = async (label, color, bg, border, insertAt) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ band_key: "new_band", label, color, bg, border }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.badge && insertAt !== undefined) {
          const ids = badges.map((b) => b.id);
          ids.splice(insertAt, 0, data.badge.id);
          await fetch(`${API_BASE_URL}/processing-badges/reorder`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ order: ids }),
          });
        }
        await fetchBadges();
        setNeedsPublish(true);
      }
    } catch {
      setBadgesError("Failed to add badge.");
    }
  };

  const handleReorderBadges = async (index, direction) => {
    const newBadges = [...badges];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newBadges.length) return;
    [newBadges[index], newBadges[swapIndex]] = [newBadges[swapIndex], newBadges[index]];
    const reorderedIds = newBadges.map((b) => b.id);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: reorderedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.badges) setBadges(data.badges);
            setNeedsPublish(true);
      }
    } catch {
      setBadgesError("Failed to reorder badges.");
    }
  };

  const handleArrayDragDrop = (field, fromIdx, toIdx) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      updated[field] = arr;
      return updated;
    });
  };

  const handleBadgeDragDrop = async (fromIdx, toIdx) => {
    const newBadges = [...badges];
    const [moved] = newBadges.splice(fromIdx, 1);
    newBadges.splice(toIdx, 0, moved);
    const reorderedIds = newBadges.map((b) => b.id);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/processing-badges/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: reorderedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.badges) setBadges(data.badges);
        setNeedsPublish(true);
      }
    } catch {
      setBadgesError("Failed to reorder badges.");
    }
  };

  const handleTipDragDrop = async (fromIdx, toIdx) => {
    const newTips = [...tips];
    const [moved] = newTips.splice(fromIdx, 1);
    newTips.splice(toIdx, 0, moved);
    const reorderedIds = newTips.map((t) => t.id);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/application-tips/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: reorderedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.tips) setTips(data.tips);
        setNeedsPublish(true);
      }
    } catch {
      setTipsError("Failed to reorder tips.");
    }
  };

  const [badgeSearch, setBadgeSearch] = useState("");
  const [badgeSearchOpen, setBadgeSearchOpen] = useState(false);
  const badgeSearchRef = useRef(null);
  const [editingBadge, setEditingBadge] = useState(null);
  const [editingFlag, setEditingFlag] = useState(null);
  const [pendingFlagInsert, setPendingFlagInsert] = useState(null);

  useEffect(() => {
    if (badgeSearchOpen && badgeSearchRef.current) badgeSearchRef.current.focus();
  }, [badgeSearchOpen]);

  useEffect(() => {
    if (activeTab !== "processingBadges") { setBadgeSearch(""); setBadgeSearchOpen(false); }
  }, [activeTab]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        setContent(data.section.content);
        snapshotRef.current = JSON.stringify(data.section.content);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(REQUIREMENTS_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(REQUIREMENTS_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const pushPayload = {
        visaMetaGrid: content?.visaMetaGrid || [],
        visaRoutes: visaRoutes.map((r) => ({
          passportName: r.passportName || "",
          destinationName: r.destinationName || "",
          processingTime: r.processingTime || "",
          visaMeta: (r.visaMeta || []).map((m) => ({ ...m })),
          visaFlags: (r.visaFlags || []).map((f) => ({ label: f.label, text: f.text, bg: f.bg, border: f.border })),
          processingBadges: (r.processingBadges || []).map((b) => ({ label: b.label, color: b.color, bg: b.bg, border: b.border })),
          loaderCheckpoints: (r.loaderCheckpoints || []).map((lc) => ({ label: lc.label, pct: lc.pct })),
          documents: (r.documents || []).map((d) => ({ key: d.key, label: d.label, description: d.detail || d.description || "" })),
          tips: (r.tips || []).map((t) => {
            const text = t.text || t.tip_text || "";
            const match = tips.find(tip => tip.tip_text === text);
            return { label: match ? match.label : (t.label || text), text };
          }),
        })),
        visaFlags: content?.visaFlags || [],
        fallbackDocs: content?.fallbackDocs || [],
        requiredDocs: content?.requiredDocs || [],
        loaderCheckpoints: content?.loaderCheckpoints || [],
        applicationTips: tips.map((t) => ({ label: t.label || t.tip_text || "", text: t.tip_text || t.label || "" })),
        processingBadges: badges.map((b) => ({ label: b.label, color: b.color, bg: b.bg, border: b.border || b.color })),
        countries: content?.countries || [],
      };
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: pushPayload }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/pull-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        if (data.content?.visaRoutes) {
          setVisaRoutes(data.content.visaRoutes.map(r => ({
            ...r,
            documents: (r.documents || []).map(d => ({ ...d, detail: d.detail || d.description || "" })),
          })));
        }
        if (data.content?.applicationTips) {
          setTips(data.content.applicationTips.map((t, i) => ({ id: "pulled_" + i, label: t.label || "", tip_text: t.text || t.label || "" })));
        }
        if (data.content?.processingBadges) {
          setBadges(data.content.processingBadges.map((b, i) => ({ id: "pulled_" + i, label: b.label || "", color: b.color || "", bg: b.bg || "", border: b.border || b.color || "" })));
        }
        // Sync pulled visa routes to the visa_requirements table (what the public site reads)
        if (data.content?.visaRoutes && Array.isArray(data.content.visaRoutes)) {
          (async () => {
            try {
              const syncToken = sessionStorage.getItem("tvc_token");
              // Fetch current routes from DB to match by passport+destionation
              const routesRes = await fetch(`${API_BASE_URL}/visa-routes`, {
                headers: { Authorization: `Bearer ${syncToken}` },
              });
              const routesData = await routesRes.json().catch(() => ({ routes: [] }));
              const existingRoutes = routesData.routes || [];
              // Build a lookup key from passport+destionation
              const existingMap = {};
              for (const er of existingRoutes) {
                const pk = (er.passportName || er.passport_name || "").trim().toLowerCase();
                const dk = (er.destinationName || er.destination_name || "").trim().toLowerCase();
                if (pk && dk) existingMap[pk + "::" + dk] = er;
              }

              for (const r of data.content.visaRoutes) {
                const rPk = (r.passportName || r.passport_name || "").trim().toLowerCase();
                const rDk = (r.destinationName || r.destination_name || "").trim().toLowerCase();
                const matchKey = rPk + "::" + rDk;
                const existing = existingMap[matchKey];
                const routeId = existing ? existing.id : null;

                const method = routeId ? "PUT" : "POST";
                const url = routeId
                  ? `${API_BASE_URL}/visa-routes/${routeId}`
                  : `${API_BASE_URL}/visa-routes`;
                const body = {
                  passportName: r.passportName || r.passport_name || "",
                  destinationName: r.destinationName || r.destination_name || "",
                  processingTime: r.processingTime || r.processing_time || "",
                  visaMeta: r.visaMeta || [],
                  visaFlags: r.visaFlags || [],
                  processingBadges: r.processingBadges || [],
                  loaderCheckpoints: r.loaderCheckpoints || [],
                  tips: r.tips || [],
                  documents: (r.documents || []).map(d => ({ key: d.key || d.label || "", label: d.label || d.key || "", detail: d.detail || d.description || "" })),
                };
                const sr = await fetch(url, {
                  method,
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${syncToken}` },
                  body: JSON.stringify(body),
                });
                if (!sr.ok) {
                  const errBody = await sr.text().catch(() => "");
                  console.error(`Failed to sync visa route "${r.passportName || r.passport_name}" → "${r.destinationName || r.destination_name}": ${sr.status} ${errBody.substring(0, 200)}`);
                }
              }
              // Re-fetch routes from DB to confirm sync
              await fetchVisaRoutes();
            } catch (syncErr) {
              console.error("Background visa route sync after sheet pull failed:", syncErr);
            }
          })();
        }
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      arr[index] = { ...(arr[index] || {}), [key]: value };
      updated[field] = arr;
      return updated;
    });
  };

  const handleOpenAddRowForm = (field, insertIndex) => {
    const columns = REQUIREMENTS_COLUMNS[activeTab] || [];
    const emptyRow = {};
    for (const col of columns) {
      emptyRow[col.key] = "";
    }
    setEditingField(field);
    setEditingIndex(null);
    setEditingItem(emptyRow);
    setPendingRowInsert(insertIndex);
    setShowRowForm(true);
  };

  const handleOpenEditRowForm = (field, index) => {
    const items = content?.[field] || [];
    setEditingField(field);
    setEditingIndex(index);
    setEditingItem({ ...items[index] });
    setPendingRowInsert(null);
    setShowRowForm(true);
  };

  const handleCloseRowForm = () => {
    setShowRowForm(false);
    setEditingField(null);
    setEditingIndex(null);
    setEditingItem({});
    setPendingRowInsert(null);
  };

  const handleRowFormFieldChange = (key, value) => {
    setEditingItem((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveRowForm = () => {
    const field = editingField;
    if (!field) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (editingIndex !== null) {
        arr[editingIndex] = { ...arr[editingIndex], ...editingItem };
      } else if (pendingRowInsert !== null && pendingRowInsert >= 0 && pendingRowInsert < arr.length) {
        arr.splice(pendingRowInsert, 0, { ...editingItem });
      } else {
        arr.push({ ...editingItem });
      }
      updated[field] = arr;
      return updated;
    });
    handleCloseRowForm();
  };

  const handleAddRowBefore = (field, index) => {
    handleOpenAddRowForm(field, index);
  };

  const handleAddRowAfter = (field, index) => {
    handleOpenAddRowForm(field, index + 1);
  };

  const openVisaRouteForm = async (item, insertIndex) => {
    let countries = dbCountries;
    if (countries.length === 0) {
      countries = await fetchDbCountries();
    }
    if (!countries || countries.length === 0) {
      setMessage("Could not load country list from database. Saving routes may fail.");
    }
    setVisaRouteEditingItem(item ? { ...item } : { passportName: "", destinationName: "", processingTime: "", visaMeta: [], visaFlags: [], processingBadges: [], loaderCheckpoints: [], tips: [], documents: [] });
    setVisaRouteEditIndex(item !== null ? insertIndex : null);
    setVisaRoutePendingInsert(item === null ? insertIndex : null);
    setVisaRouteFormOpen(true);
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = (updated[field] || []).filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, REQUIREMENTS_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => b.duplicateIndex - a.duplicateIndex);
    for (const dup of sorted) {
      const arr = [...(cleanContent[dup.field] || [])];
      if (arr[dup.duplicateIndex]) {
        arr.splice(dup.duplicateIndex, 1);
        cleanContent[dup.field] = arr;
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = overrideContent || { ...content };
    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        setNeedsPublish(false);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderVisaMetaGrid = () => {
    const items = content?.visaMetaGrid || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Visa Meta</h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => {
                setContent((prev) => {
                  const updated = { ...prev };
                  const arr = [...(updated.visaMetaGrid || [])];
                  arr.push({ key: "", label: "" });
                  updated.visaMetaGrid = arr;
                  return updated;
                });
              }}
            >
              Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                <th>Meta Key</th>
                <th>Label</th>
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  const q = arraySearch.trim().toLowerCase();
                  return (item.key || "").toLowerCase().includes(q) || (item.label || "").toLowerCase().includes(q);
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field: "visaMetaGrid", fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== "visaMetaGrid" || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated.visaMetaGrid || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated.visaMetaGrid = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp("visaMetaGrid", idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown("visaMetaGrid", idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                    </div>
                  </td>
                  <td style={{verticalAlign:"top",textAlign:"left"}}>
                    <input
                      className="AdminInput"
                      type="text"
                      value={item.key || ""}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setContent((prev) => {
                          const updated = { ...prev };
                          const arr = [...(updated.visaMetaGrid || [])];
                          arr[idx] = { ...arr[idx], key: newVal };
                          updated.visaMetaGrid = arr;
                          return updated;
                        });
                      }}
                      style={{width:"100%",boxSizing:"border-box"}}
                    />
                  </td>
                  <td style={{verticalAlign:"top",textAlign:"left"}}>
                    <input
                      className="AdminInput"
                      type="text"
                      value={item.label || ""}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setContent((prev) => {
                          const updated = { ...prev };
                          const arr = [...(updated.visaMetaGrid || [])];
                          arr[idx] = { ...arr[idx], label: newVal };
                          updated.visaMetaGrid = arr;
                          return updated;
                        });
                      }}
                      style={{width:"100%",boxSizing:"border-box"}}
                    />
                  </td>
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore("visaMetaGrid", idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter("visaMetaGrid", idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow("visaMetaGrid", idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          (item.key || "").toLowerCase().includes(arraySearch.trim().toLowerCase()) ||
          (item.label || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  const renderArrayEditor = (field, columns, disableDrag) => {
    if (!columns) {
      return <div className="AdminCardMb"><div className="AdminEmptyState">No column definitions for "{field}".</div></div>;
    }
    const items = content?.[field] || [];
    const displayItems = field === "loaderCheckpoints" ? [...items].sort((a, b) => (parseFloat(a.pct) || 0) - (parseFloat(b.pct) || 0)) : items;
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayItems
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  {...(disableDrag ? {} : {
                    draggable: "true",
                    onDragStart: (e) => {
                      dragRef.current = { type: "array", field, fromIndex: idx };
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                      e.currentTarget.style.opacity = "0.4";
                    },
                    onDragOver: (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.style.outline = "2px dashed #3b82f6";
                      e.currentTarget.style.outlineOffset = "-2px";
                    },
                    onDragLeave: (e) => { e.currentTarget.style.outline = ""; },
                    onDrop: (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                      const { field: fromField, fromIndex } = dragRef.current;
                      if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                      setContent((prev) => {
                        const updated = { ...prev };
                        const arr = [...(updated[field] || [])];
                        const [moved] = arr.splice(fromIndex, 1);
                        arr.splice(idx, 0, moved);
                        updated[field] = arr;
                        return updated;
                      });
                      dragRef.current = null;
                    },
                    onDragEnd: (e) => {
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      dragRef.current = null;
                    },
                  })}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleOpenEditRowForm(field, idx)} title="Edit row" style={{marginLeft:4}}>&#9998;</button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} style={{verticalAlign:"top",textAlign:"left"}}>
                      <span className="AdminTableCellText">{item[col.key] || ""}</span>
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {displayItems.length === 0 && (
          <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.emptyArray}</div>
        )}
        {displayItems.length > 0 && arraySearch.trim() && !displayItems.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current || needsPublish;
  const orderedTabs = [...REQUIREMENTS_TABS].sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Requirements Content
          {activeTab !== "data" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div className="AdminHeaderActionsRow">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminTabBar" ref={tabBarRef} style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {orderedTabs.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { dragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = dragRef.current;
              if (!fromId || fromId === tab.id) return;
              const newOrder = [...tabOrder];
              const fromIdx = newOrder.indexOf(fromId);
              const toIdx = newOrder.indexOf(tab.id);
              newOrder.splice(fromIdx, 1);
              newOrder.splice(toIdx, 0, fromId);
              setTabOrder(newOrder);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; dragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "requirements"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(REQUIREMENTS_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "visaMetaGrid" && renderVisaMetaGrid()}
      {activeTab === "visaRoutes" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Visa Routes</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={arraySearch}
                  onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => { setArraySearch(""); setTimeout(() => setSearchOpen(false), 200) }}
              
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                onClick={() => openVisaRouteForm(null, visaRoutes.length)}
              >
                Add Row
              </button>
            </div>
          </div>
          {(visaRoutes.length === 0) ? (
            <div className="AdminEmptyState">No visa routes found.</div>
          ) : (
            <TableWithScrollButtons>
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Passport</th>
                    <th>Destination</th>
                    <th style={{width:140,textAlign:"center"}}>Processing Time</th>
                    <th style={{textAlign:"center"}}>Meta</th>
                    <th style={{textAlign:"center"}}>Flags</th>
                    <th style={{textAlign:"center"}}>Badges</th>
                    <th style={{textAlign:"center"}}>Checkpoints</th>
                    <th style={{textAlign:"center"}}>Docs</th>
                    <th style={{textAlign:"center"}}>Tips</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
    {visaRoutes
                    .filter((route) => {
                      if (!arraySearch.trim()) return true;
                      const q = arraySearch.trim().toLowerCase();
                      return (route.passportName || "").toLowerCase().includes(q);
                    })
                    .map((route, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowUp("visaRoutes", idx)} disabled={idx === 0} title="Move up">↑</button>
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowDown("visaRoutes", idx)} disabled={idx === visaRoutes.length - 1} title="Move down">↓</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => openVisaRouteForm(route, idx)} title="Edit" style={{marginLeft:4}}>✎</button>
                        </div>
                      </td>
                      <td style={{fontWeight:600}}>{route.passportName || ""}</td>
                      <td>{route.destinationName || ""}</td>
                      <td><div style={{maxWidth:140,wordBreak:"break-word"}}>{route.processingTime || ""}</div></td>
                      <td style={{textAlign:"center"}}>
                        {(route.visaMeta || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default"}} title={(route.visaMeta || []).map((m) => (m.label || m.key) + (m.value ? ": "+m.value : "")).join(", ")}>
                            {(route.visaMeta || []).length}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td style={{textAlign:"center"}}>
                        {(route.visaFlags || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default"}} title={(route.visaFlags || []).map((f) => f.label).join(", ")}>
                            {(route.visaFlags || []).length}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td style={{textAlign:"center"}}>
                        {(route.processingBadges || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default",background:"#dbeafe",color:"#1e40af"}}>
                            {(route.processingBadges || []).length > 0 ? route.processingBadges[0].label : ""}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td style={{textAlign:"center"}}>
                        {(route.loaderCheckpoints || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default",background:"#fef3c7",color:"#92400e"}} title={(route.loaderCheckpoints || []).map((lc) => (lc.label || "") + (lc.pct ? " ("+lc.pct+"%)" : "")).join(", ")}>
                            {(route.loaderCheckpoints || []).length}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td style={{textAlign:"center"}}>
                        {(route.documents || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default",background:"#ede9fe",color:"#5b21b6"}} title={(route.documents || []).map((d) => d.label || d.key).join(", ")}>
                            {(route.documents || []).length}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td style={{textAlign:"center"}}>
                        {(route.tips || []).length > 0 ? (
                          <span className="AdminVisaMetaPill" style={{cursor:"default",background:"#fce7f3",color:"#9d174d"}} title={(route.tips || []).map((t) => typeof t === "string" ? t : (t.label || t.tip_text || t.text || "")).join(", ")}>
                            {(route.tips || []).length}
                          </span>
                        ) : <span style={{color:"#94a3b8",fontSize:12}}>--</span>}
                      </td>
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => openVisaRouteForm(null, idx)} title="Add row before">▲+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => openVisaRouteForm(null, idx + 1)} title="Add row after">▼+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => { const route = visaRoutes[idx]; setVisaRoutes(prev => prev.filter((_, i) => i !== idx)); setContent(prev => ({ ...prev, visaRoutes: (prev.visaRoutes || []).filter((_, i) => i !== idx) })); setNeedsPublish(true); if (route?.id) { fetch(`${API_BASE_URL}/visa-routes/${route.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` } }).catch(() => {}); } }} title="Delete row">✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWithScrollButtons>
          )}
          {visaRoutes.length === 0 && (
            <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.emptyArray}</div>
          )}
          {visaRoutes.length > 0 && arraySearch.trim() && !visaRoutes.some((route) => {
            const q = arraySearch.trim().toLowerCase();
            return (route.passportName || "").toLowerCase().includes(q) ||
                   (route.destinationName || "").toLowerCase().includes(q) ||
                   (route.processingTime || "").toLowerCase().includes(q);
          }) && (
            <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
          )}
        </div>
      )}
      
      {visaRouteFormOpen && (
        <VisaRouteFormOverlay
          editingItem={visaRouteEditingItem}
          availableMetas={content?.visaMetaGrid || []}
          availableFlags={content?.visaFlags || []}
          availableBadges={badges}
          availableLoaderCheckpoints={content?.loaderCheckpoints || []}
          availableTips={tips}
          availableRequiredDocs={content?.requiredDocs || []}
           availableCountries={content?.countries || []}
          onSave={(savedItem) => {
            handleVisaRouteSave(savedItem);
          }}
          onClose={() => {
            setVisaRouteFormOpen(false);
            setVisaRouteEditingItem(null);
            setVisaRouteEditIndex(null);
            setVisaRoutePendingInsert(null);
          }}
        />
      )}
      {activeTab === "visaFlags" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Visa Flags</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${badgeSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={badgeSearchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={badgeSearch}
                  onChange={(e) => setBadgeSearch(e.target.value)}
                   onBlur={() => { setBadgeSearch(""); setTimeout(() => setBadgeSearchOpen(false), 200) }}
                
                />
                {!badgeSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setBadgeSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn" onClick={() => setEditingFlag({ label: "", text: "#000000", bg: "#ffffff", border: "#000000" })}>Add Row</button>
            </div>
          </div>
          {(!content?.visaFlags || content.visaFlags.length === 0) ? (
            <div className="AdminEmptyState">No flags found.</div>
          ) : (
            <TableWithScrollButtons>
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Label</th>
                    <th>Text Color</th>
                    <th>BG Color</th>
                    <th>Border Color</th>
                    <th>Preview</th>
                    <th className="AdminThOrder">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(content?.visaFlags || [])
                    .filter((flag) => {
                      if (!badgeSearch.trim()) return true;
                      const q = badgeSearch.trim().toLowerCase();
                      return (flag.label || "").toLowerCase().includes(q);
                    })
                    .map((flag, idx) => (
                      <FlagRow
                        key={idx}
                        flag={flag}
                        idx={idx}
                        total={content.visaFlags.length}
                        dragRef={dragRef}
                        onDropItem={handleArrayDragDrop}
                        onEdit={(f, i) => setEditingFlag({ ...f, _index: i })}
                        onMoveUp={() => handleMoveRowUp("visaFlags", idx)}
                        onMoveDown={() => handleMoveRowDown("visaFlags", idx)}
                        onAddBefore={() => {
                          setPendingFlagInsert({ before: idx });
                          setEditingFlag({ label: "", text: "#000000", bg: "#ffffff", border: "#000000" });
                        }}
                        onAddAfter={() => {
                          setPendingFlagInsert({ after: idx });
                          setEditingFlag({ label: "", text: "#000000", bg: "#ffffff", border: "#000000" });
                        }}
                        onDelete={() => handleDeleteRow("visaFlags", idx)}
                      />
                    ))}
                </tbody>
              </table>
            </TableWithScrollButtons>
          )}
          {content?.visaFlags?.length > 0 && badgeSearch.trim() && !(content.visaFlags || []).some((f) =>
            (f.label || "").toLowerCase().includes(badgeSearch.trim().toLowerCase())
          ) && (
            <div className="AdminEmptyState">No results found for "{badgeSearch}"</div>
          )}
        </div>
      )}
      {editingFlag && (
        <div className="AdminOverlay" onClick={() => { setEditingFlag(null); setPendingFlagInsert(null); }}>
          <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>{editingFlag._index !== undefined ? "Edit" : "Add"} Visa Flag</h3>
              <button className="AdminIconBtn" onClick={() => { setEditingFlag(null); setPendingFlagInsert(null); }} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                <div>
                  <label className="AdminFieldLabel">Display Label</label>
                  <input
                    className="AdminInput AdminInputBlock"
                    value={editingFlag.label}
                    onChange={(e) => setEditingFlag({ ...editingFlag, label: e.target.value })}
                    placeholder="Flag label"
                  />
                </div>
                <div>
                  <label className="AdminFieldLabel">Text Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingFlag.text} onChange={(e) => setEditingFlag({ ...editingFlag, text: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingFlag.text} onChange={(e) => setEditingFlag({ ...editingFlag, text: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
                <div>
                  <label className="AdminFieldLabel">BG Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingFlag.bg} onChange={(e) => setEditingFlag({ ...editingFlag, bg: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingFlag.bg} onChange={(e) => setEditingFlag({ ...editingFlag, bg: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
                <div>
                  <label className="AdminFieldLabel">Border Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingFlag.border||editingFlag.text} onChange={(e) => setEditingFlag({ ...editingFlag, border: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingFlag.border||editingFlag.text} onChange={(e) => setEditingFlag({ ...editingFlag, border: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column"}}>
                <label className="AdminFieldLabel">Preview</label>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:20,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",minHeight:80,flex:1}}>
                  <span style={{background:editingFlag.bg,color:editingFlag.text,border:"1px solid "+(editingFlag.border||editingFlag.text),borderRadius:6,padding:"4px 14px",fontSize:14,fontWeight:600,display:"inline-block"}}>
                    {editingFlag.label || "Flag Label"}
                  </span>
                </div>
              </div>
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
              <button
                className="AdminButton"
                style={{width:100}}
                onClick={() => {
                  const updated = [...(content.visaFlags || [])];
                  const newFlag = { label: editingFlag.label, text: editingFlag.text, bg: editingFlag.bg, border: editingFlag.border };
                  if (editingFlag._index !== undefined) {
                    updated[editingFlag._index] = newFlag;
                  } else if (pendingFlagInsert) {
                    const insertIdx = pendingFlagInsert.before !== undefined ? pendingFlagInsert.before : pendingFlagInsert.after;
                    updated.splice(insertIdx, 0, newFlag);
                  } else {
                    updated.push(newFlag);
                  }
                  setContent({ ...content, visaFlags: updated });
                  setEditingFlag(null);
                  setPendingFlagInsert(null);
                }}
                disabled={!editingFlag.label.trim()}
              >{editingFlag._index !== undefined ? "Save" : "Add"}</button>
            </div>
          </div>
        </div>
      )}
      {activeTab === "loaderCheckpoints" && renderArrayEditor("loaderCheckpoints", REQUIREMENTS_COLUMNS.loaderCheckpoints, true)}
      {activeTab === "processingBadges" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Processing Badges</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${badgeSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={badgeSearchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={badgeSearch}
                  onChange={(e) => setBadgeSearch(e.target.value)}
                   onBlur={() => { setBadgeSearch(""); setTimeout(() => setBadgeSearchOpen(false), 200) }}
                
                />
                {!badgeSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setBadgeSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn" onClick={() => handleAddBadge()}>
                Add Row
              </button>
            </div>
          </div>
          {badgesError && <p className="AdminMsgBox AdminError">{badgesError}</p>}
          {badgesLoading ? (
            <div className="AdminEmptyState">Loading badges...</div>
          ) : badges.length === 0 ? (
            <div className="AdminEmptyState">No badges found.</div>
          ) : (
            <TableWithScrollButtons>
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Label</th>
                    <th>Color</th>
                    <th>BG Color</th>
                    <th>Border</th>
                    <th>Preview</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {badges
                    .filter((badge) => {
                      if (!badgeSearch.trim()) return true;
                      const q = badgeSearch.trim().toLowerCase();
                      return badge.label.toLowerCase().includes(q) || badge.color.toLowerCase().includes(q) || badge.bg.toLowerCase().includes(q);
                    })
                    .map((badge) => {
                      const actualIdx = badges.findIndex((b) => b.id === badge.id);
                      return (
                        <BadgeRow
                          key={badge.id}
                          badge={badge}
                          idx={actualIdx}
                          total={badges.length}
                          dragRef={dragRef}
                          onDropItem={handleBadgeDragDrop}
                          onDelete={handleDeleteBadge}
                          onReorder={handleReorderBadges}
                          onAddBefore={(idx) => handleAddBadge(idx)}
                          onAddAfter={(idx) => handleAddBadge(idx + 1)}
                          onEdit={(badge) => setEditingBadge({ ...badge })}
                        />
                      );
                    })}
                </tbody>
              </table>
            </TableWithScrollButtons>
          )}
          {badges.length > 0 && badgeSearch.trim() && !badges.some((b) => {
            const q = badgeSearch.trim().toLowerCase();
            return b.label.toLowerCase().includes(q) || b.color.toLowerCase().includes(q) || b.bg.toLowerCase().includes(q);
          }) && (
            <div className="AdminEmptyState">No results found for "{badgeSearch}"</div>
          )}
        </div>
      )}
      {editingBadge && (
        <div className="AdminOverlay" onClick={() => setEditingBadge(null)}>
          <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>{editingBadge.isNew ? "Add Badge" : "Edit Badge"}</h3>
              <button className="AdminIconBtn" onClick={() => setEditingBadge(null)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                <div>
                  <label className="AdminFieldLabel">Label</label>
                  <input
                    className="AdminInput AdminInputBlock"
                    value={editingBadge.label}
                    onChange={(e) => setEditingBadge({ ...editingBadge, label: e.target.value })}
                    placeholder="Badge label"
                  />
                </div>
                <div>
                  <label className="AdminFieldLabel">Text Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingBadge.color} onChange={(e) => setEditingBadge({ ...editingBadge, color: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingBadge.color} onChange={(e) => setEditingBadge({ ...editingBadge, color: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
                <div>
                  <label className="AdminFieldLabel">BG Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingBadge.bg} onChange={(e) => setEditingBadge({ ...editingBadge, bg: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingBadge.bg} onChange={(e) => setEditingBadge({ ...editingBadge, bg: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
                <div>
                  <label className="AdminFieldLabel">Border Color</label>
                  <div className="AdminFlexRowGap8">
                    <input type="color" value={editingBadge.border||editingBadge.color} onChange={(e) => setEditingBadge({ ...editingBadge, border: e.target.value })} style={{width:40,height:34,padding:0,border:"1px solid #d1d5db",borderRadius:4,cursor:"pointer"}} />
                    <input className="AdminInput AdminInputBlock" value={editingBadge.border||editingBadge.color} onChange={(e) => setEditingBadge({ ...editingBadge, border: e.target.value })} placeholder="#hex" style={{fontFamily:"monospace"}} />
                  </div>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column"}}>
                <label className="AdminFieldLabel">Preview</label>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:20,border:"1px solid #e2e8f0",borderRadius:8,background:"#f8fafc",minHeight:80,flex:1}}>
                  <span className="AdminBadgePreview" style={{background:editingBadge.bg,color:editingBadge.color,border:"1px solid "+(editingBadge.border||editingBadge.color),borderRadius:6,padding:"4px 14px",fontSize:14,fontWeight:600,display:"inline-block"}}>
                    {editingBadge.label || "Badge Label"}
                  </span>
                </div>
              </div>
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
              <button
                className="AdminButton"
                style={{width:100}}
                onClick={() => {
                  if (editingBadge.isNew) {
                    handleCreateBadge(editingBadge.label.trim(), editingBadge.color, editingBadge.bg, editingBadge.border||editingBadge.color, editingBadge.insertAt);
                  } else {
                    handleUpdateBadge(editingBadge.id, editingBadge.label.trim(), editingBadge.color, editingBadge.bg, editingBadge.border||editingBadge.color);
                  }
                  setEditingBadge(null);
                }}
                disabled={!editingBadge.label.trim()}
              >Save</button>
            </div>
          </div>
        </div>
      )}
      {activeTab === "fallbackDocs" && renderArrayEditor("fallbackDocs", REQUIREMENTS_COLUMNS.fallbackDocs)}
      {activeTab === "requiredDocs" && renderArrayEditor("requiredDocs", REQUIREMENTS_COLUMNS.requiredDocs)}
      {activeTab === "countries" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Countries</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={arraySearch}
                  onChange={(e) => setArraySearch(e.target.value)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                onClick={() => {
                  setContent((prev) => ({
                    ...prev,
                    countries: [...(prev?.countries || []), { name: "", region: "", code: "" }],
                  }));
                }}
              >
                Add Row
              </button>
            </div>
          </div>
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Order</th>
                  <th>Name</th>
                  <th>Region</th>
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(content?.countries || [])
                  .map((item, idx) => ({ item, idx }))
                  .filter(({ item }) => {
                    if (!arraySearch.trim()) return true;
                    const q = arraySearch.trim().toLowerCase();
                    return (item.name || "").toLowerCase().includes(q) || (item.region || "").toLowerCase().includes(q);
                  })
                  .map(({ item, idx }) => (
                    <tr
                      key={idx}
                      draggable="true"
                      onDragStart={(e) => {
                        dragRef.current = { type: "array", field: "countries", fromIndex: idx };
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                        e.currentTarget.style.opacity = "0.4";
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.style.outline = "2px dashed #3b82f6";
                        e.currentTarget.style.outlineOffset = "-2px";
                      }}
                      onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.opacity = "";
                        e.currentTarget.style.outline = "";
                        if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                        const { field: fromField, fromIndex } = dragRef.current;
                        if (fromField !== "countries" || fromIndex === idx) { dragRef.current = null; return; }
                        setContent((prev) => {
                          const updated = { ...prev };
                          const arr = [...(updated.countries || [])];
                          const [moved] = arr.splice(fromIndex, 1);
                          arr.splice(idx, 0, moved);
                          updated.countries = arr;
                          return updated;
                        });
                        dragRef.current = null;
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = "";
                        e.currentTarget.style.outline = "";
                        dragRef.current = null;
                      }}
                    >
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => {
                            const arr = [...(content?.countries || [])];
                            if (idx === 0) return;
                            [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }} disabled={idx === 0} title="Move up">&#8593;</button>
                          <button className="AdminMoveBtn" onClick={() => {
                            const arr = [...(content?.countries || [])];
                            if (idx === arr.length - 1) return;
                            [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }} disabled={idx === (content?.countries || []).length - 1} title="Move down">&#8595;</button>
                        </div>
                      </td>
                      <td>
                        <input
                          className="AdminInput"
                          type="text"
                          value={item.name || ""}
                          onChange={(e) => {
                            const arr = [...(content?.countries || [])];
                            arr[idx] = { ...arr[idx], name: e.target.value };
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }}
                          style={{ width: "100%", boxSizing: "border-box" }}
                        />
                      </td>
                      <td>
                        <input
                          className="AdminInput"
                          type="text"
                          value={item.region || ""}
                          onChange={(e) => {
                            const arr = [...(content?.countries || [])];
                            arr[idx] = { ...arr[idx], region: e.target.value };
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }}
                          style={{ width: "100%", boxSizing: "border-box" }}
                        />
                      </td>
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => {
                            const arr = [...(content?.countries || [])];
                            arr.splice(idx, 0, { name: "", region: "", code: "" });
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }} title="Add row before">&#9650;+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => {
                            const arr = [...(content?.countries || [])];
                            arr.splice(idx + 1, 0, { name: "", region: "", code: "" });
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }} title="Add row after">&#9660;+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => {
                            const arr = [...(content?.countries || [])];
                            arr.splice(idx, 1);
                            setContent((prev) => ({ ...prev, countries: arr }));
                          }} title="Delete row">&#10005;</button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </TableWithScrollButtons>
          {(content?.countries || []).length === 0 && (
            <div className="AdminEmptyState">{REQUIREMENTS_MESSAGES.emptyArray}</div>
          )}
        </div>
      )}
      {activeTab === "applicationTips" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Application Tips</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={tipSearchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search tips..."
                  value={tipSearch}
                  onChange={(e) => setTipSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn" onClick={handleAddTip}>
                Add Row
              </button>
            </div>
          </div>
          {tipsError && <p className="AdminMsgBox AdminError">{tipsError}</p>}
          {tipsLoading ? (
            <div className="AdminEmptyState">Loading tips...</div>
          ) : tips.length === 0 ? (
            <div className="AdminEmptyState">No tips. Click "Add Tip" to create one.</div>
          ) : (
            <TableWithScrollButtons>
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Label</th>
                    <th>Description</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tips
                    .filter((tip) => {
                      if (!tipSearch.trim()) return true;
                      return (tip.tip_text || "").toLowerCase().includes(tipSearch.trim().toLowerCase());
                    })
                    .map((tip, idx) => (
                    <tr key={tip.id}>
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => handleMoveTip(idx, -1)} disabled={idx === 0} title="Move up">&#8593;</button>
                          <button className="AdminMoveBtn" onClick={() => handleMoveTip(idx, 1)} disabled={idx === tips.length - 1} title="Move down">&#8595;</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleStartEdit(tip)} title="Edit" style={{marginLeft:4}}>&#9998;</button>
                        </div>
                      </td>
                      <td>
                        <span style={{fontSize:13,fontWeight:600,color:"#1B2A4A"}}>{tip.label || ""}</span>
                      </td>
                      <td>
                        <div className="AdminTipText" style={{ whiteSpace: "pre-wrap" }}>
                          {tip.tip_text}
                        </div>
                      </td>
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => setEditingTipOverlay({ id: null, text: "", _insertAt: idx })} title="Add row before">&#9650;+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => setEditingTipOverlay({ id: null, text: "", _insertAt: idx + 1 })} title="Add row after">&#9660;+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteTip(tip.id)} title="Delete">&#10005;</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWithScrollButtons>
          )}
          {tips.length > 0 && tipSearch.trim() && !tips.some((t) =>
            (t.tip_text || "").toLowerCase().includes(tipSearch.trim().toLowerCase())
          ) && (
            <div className="AdminEmptyState">No results found for "{tipSearch}"</div>
          )}
        </div>
      )}

      {editingTipOverlay && (
        <div className="AdminOverlay" onClick={() => setEditingTipOverlay(null)}>
          <div className="AdminOverlayCard" style={{maxWidth:520}} onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>{editingTipOverlay.id ? "Edit" : "Add"} Application Tip</h3>
              <button className="AdminIconBtn" onClick={() => setEditingTipOverlay(null)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"flex",flexDirection:"column",gap:16}}>
              <div>
                <label className="AdminFieldLabel">Label</label>
                <input
                  className="AdminInput AdminInputBlock"
                  value={editingTipOverlay.label || ""}
                  onChange={(e) => setEditingTipOverlay({ ...editingTipOverlay, label: e.target.value })}
                  placeholder="Short label for the tip"
                />
              </div>
              <div>
                <label className="AdminFieldLabel">Description</label>
                <textarea
                  className="AdminInput AdminInputBlock"
                  style={{minHeight:100,resize:"vertical"}}
                  value={editingTipOverlay.text}
                  onChange={(e) => setEditingTipOverlay({ ...editingTipOverlay, text: e.target.value })}
                  placeholder="Enter tip description"
                />
              </div>
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
              <button
                className="AdminButton"
                style={{width:100}}
                onClick={() => handleSaveEdit(editingTipOverlay.id)}
                disabled={!editingTipOverlay.text.trim()}
              >{editingTipOverlay.id ? "Save" : "Add"}</button>
            </div>
          </div>
        </div>
      )}

      {showRowForm && (
        <div className="AdminOverlay" onClick={handleCloseRowForm}>
          <div className="AdminOverlayCard" style={{maxWidth:560}} onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>{editingIndex !== null ? "Edit" : "Add"} Row</h3>
              <button className="AdminIconBtn" onClick={handleCloseRowForm} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {(REQUIREMENTS_COLUMNS[editingField] || []).map((col) => (
                <div key={col.key} style={col.type === "textarea" ? {gridColumn:"1 / -1"} : {}}>
                  <label className="AdminFieldLabel">{col.label}</label>
                  {col.type === "select" ? (
                    <select
                      className="AdminInput AdminInputBlock"
                      value={editingItem[col.key] || ""}
                      onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {(col.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : col.type === "textarea" ? (
                    <textarea
                      className="AdminInput AdminInputBlock"
                      style={{minHeight:80,resize:"vertical"}}
                      value={editingItem[col.key] || ""}
                      onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  ) : (
                    <input
                      className="AdminInput AdminInputBlock"
                      value={editingItem[col.key] || ""}
                      onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="AdminOverlayFooter" style={{justifyContent:"center"}}>
              <button className="AdminButton" style={{width:120}} onClick={handleSaveRowForm}>
                {editingIndex !== null ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminScheduleMaxW440">
            <h3 className="AdminModalTitle">Note for Approval</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {user?.role === "Staff"
                ? "Your changes will be sent to the Manager for approval."
                : "Your changes will be sent to the Admin for approval."}
            </p>
            <textarea
              className="AdminNoteTextarea"
              placeholder="Describe what you changed and why..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="AdminNoteModalRow">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleConfirmSubmit} disabled={!noteText.trim()}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="AdminOverlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="AdminOverlayCard" style={{maxWidth:480}} onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>Duplicate Rows Found</h3>
              <button className="AdminIconBtn" onClick={() => setShowDuplicateModal(false)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20}}>
              <p className="AdminText13 AdminTextMuted AdminM0B12">
                {duplicateInfo.length} duplicate row(s) detected. You can remove them or continue anyway.
              </p>
              {duplicateInfo.map((d, i) => (
                <div key={i} className="AdminDupWarningBox">
                  <div className="AdminDupItem">
                    <strong>{d.tabLabel}</strong>: Row {d.duplicateIndex + 1} matches Row {d.existingIndex + 1}
                  </div>
                </div>
              ))}
              <div className="AdminFlexCenterG12 AdminMt16">
                <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={handleRemoveDuplicates} disabled={duplicateActionLoading}>
                  {duplicateActionLoading ? "Removing..." : "Remove Duplicates"}
                </button>
                <button className="AdminButton AdminButtonSmall" onClick={handleContinueAnyway}>Continue Anyway</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent" style={{maxWidth:400,textAlign:"center"}} onClick={(e) => e.stopPropagation()}>
            <div style={{fontSize:48,marginBottom:8}}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{marginBottom:4}}>{successTitle}</h3>
            <p style={{fontSize:13,color:"#64748b",margin:"0 0 20px 0"}}>
              {successTitle === "Changes Published" ? "Changes are now live." : "Your request has been submitted for review."}
            </p>
            <div style={{display:"flex",justifyContent:"center"}}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRequirementsEditor;
